import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { createPortal } from 'react-dom'
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from '../utils/s3Upload'

const API_URL = process.env.REACT_APP_API_URL

const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR']
const FINAL_STATUS = 'Active'
const MAX_PO_OWNER_ATTACHMENTS = 10
const MAX_PO_MILESTONE_ATTACHMENTS = 10

/** Backend rejects update-po with poStatus Approved — must use approve-po. */
const isApprovedPoStatus = (status) =>
  String(status || '')
    .trim()
    .toLowerCase() === 'approved'

/** On edit/save, Approved POs go back to Draft so they can be re-submitted for approval. */
const resolvePoStatusForUpdate = (status) => {
  if (isApprovedPoStatus(status)) return 'Draft'
  const normalized = String(status || 'Draft').trim()
  return normalized || 'Draft'
}

const DEFAULT_PR_GST_PERCENT = '18'

const emptyMaterial = () => ({
  materialCode: '',
  materialDescription: '',
  hsn: '',
  uom: '',
  quantity: '',
  referenceNumber: '',
  referenceNumber_id: '',
  customerOrderid: '',
  technicalRemarks: '',
  basicPrice: '',
  gst: DEFAULT_PR_GST_PERCENT,
  gstValue: '',
  totalValue: '',
  sourceType: 'manual'
})

function isPrMaterialRow(row) {
  if (!row || typeof row !== 'object') return false
  if (row.sourceType === 'pr') return true
  if (row.sourceType === 'rfq' || row.sourceType === 'sq' || row.sourceType === 'so' || row.sourceType === 'manual') {
    return false
  }
  const ref = String(row.referenceNumber || '').trim().toUpperCase()
  if (!ref) return false
  if (ref.startsWith('RFQ') || ref.startsWith('SQ')) return false
  return Boolean(String(row.referenceNumber_id || '').trim() || ref)
}

const emptyTerm = () => ({
  term: '',
  response: ''
})

const DEFAULT_PO_TERMS = [
  { term: 'Payment Terms', response: 'Net 30 days' },
  { term: 'Packing Forwarding', response: 'Included in Basic Price' },
  {
    term: 'Delivery Location',
    response: `InstaTenders Solutions Private Limited, No. 149/1, Gandhi Street, Kundrathur -Sriperumbudur Main Road, Kavanur,Kundrathur, 600069.
GST No : 33AAFCI2552K1ZM
Contact:
Manikandan B - 9150081259, Gokul M - 8925984388, Naresh Kannan K - 8939028055`
  },
  { term: 'Delivery Period', response: 'Immediate' },
  { term: 'Warranty Terms', response: '-' },
  { term: 'Special Instruction', response: '-' }
]

function getCurrentUserFromStorage() {
  const id = String(localStorage.getItem('id') || '').trim()
  let name = String(localStorage.getItem('name') || '').trim()
  if (!name) {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null')
      name = String(storedUser?.name || '').trim()
    } catch {
      name = ''
    }
  }
  return { id, name }
}

const emptyMilestone = () => {
  const currentUser = getCurrentUserFromStorage()
  return {
    milestoneActivity: '',
    actionToBeTaken: '',
    responsibilityUser: currentUser.name,
    responsibilityUserId: currentUser.id,
    attachments: [],
    pendingFiles: []
  }
}

function mapMilestoneAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    url: item?.url || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || ''
  }
}

function serializeMilestoneForSnapshot(milestone) {
  return {
    milestoneActivity: milestone.milestoneActivity,
    actionToBeTaken: milestone.actionToBeTaken,
    responsibilityUser: milestone.responsibilityUser,
    responsibilityUserId: milestone.responsibilityUserId,
    attachments: (milestone.attachments || []).map((att) => ({
      s3Key: att.s3Key,
      originalFileName: att.originalFileName
    })),
    pendingFileNames: (milestone.pendingFiles || []).map((file) => file.name)
  }
}

const DEFAULT_PO_MILESTONES = [
  { milestoneActivity: 'Advance terms', actionToBeTaken: '' },
  { milestoneActivity: 'Comparity statements', actionToBeTaken: '' }
]

function getDefaultMilestones() {
  const currentUser = getCurrentUserFromStorage()
  return DEFAULT_PO_MILESTONES.map((m) => ({
    milestoneActivity: m.milestoneActivity,
    actionToBeTaken: m.actionToBeTaken,
    responsibilityUser: currentUser.name,
    responsibilityUserId: currentUser.id,
    attachments: [],
    pendingFiles: []
  }))
}

function getDefaultTerms() {
  return DEFAULT_PO_TERMS.map((t) => ({ term: t.term, response: t.response }))
}

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function getTodayIsoDate() {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function formatDateForApi(isoDate) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  return `${dd}-${mm}-${yyyy}`
}

function parseDateFromApi(value) {
  if (!value) return ''
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split('-')
    return `${yyyy}-${mm}-${dd}`
  }
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    const [, dd, mm, yyyy] = dateTimeMatch
    return `${yyyy}-${mm}-${dd}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function isDeliveryBeforePoDate(poDate, poDeliveryDate) {
  if (!poDate || !poDeliveryDate) return false
  return poDeliveryDate < poDate
}

function extractCreatedPoId(responseData) {
  const data = responseData?.data
  if (!data) return responseData?.id || responseData?._id || null
  if (typeof data === 'string') return data
  return data._id || data.id || null
}

function pickPoPdfFields(source) {
  if (!source || typeof source !== 'object') {
    return { pdfLink: '', pdfDownloadUrl: '' }
  }
  return {
    pdfLink: String(source.pdfLink || '').trim(),
    pdfDownloadUrl: String(source.pdfDownloadUrl || '').trim()
  }
}

function extractPdfFieldsFromPoResponse(responseData) {
  const data = responseData?.data
  if (!data || typeof data !== 'object') return { pdfLink: '', pdfDownloadUrl: '' }
  if (data.po && typeof data.po === 'object') return pickPoPdfFields(data.po)
  return pickPoPdfFields(data)
}

function normalizePoUsers(users) {
  if (!Array.isArray(users)) return []
  return users
    .map((user) => ({
      _id: String(user._id || user.id || '').trim(),
      name: String(user.name || user.userName || user.email || '').trim(),
      email: String(user.email || user.userEmail || '').trim(),
      role: String(user.role || '').trim().toLowerCase()
    }))
    .filter((user) => user._id && user.name)
}

function extractUsersList(responseData) {
  if (!responseData || typeof responseData !== 'object') return []
  if (Array.isArray(responseData.users)) return responseData.users
  if (Array.isArray(responseData.data?.users)) return responseData.data.users
  if (Array.isArray(responseData.data)) return responseData.data
  return []
}

function isActiveUser(user) {
  return String(user?.status || '').trim().toLowerCase() === 'active'
}

function isAdminUser(user) {
  return String(user?.role || '').trim().toLowerCase() === 'admin'
}

function isDecimalInput(value) {
  return value === '' || /^\d*\.?\d*$/.test(value)
}

function formatMaterialAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function calcMaterialPricing(basicPrice, quantity, gstPercent) {
  const basic = parseFloat(basicPrice) || 0
  const qty = parseFloat(quantity) || 0
  const gst = parseFloat(gstPercent) || 0
  const lineBasic = basic * qty
  const gstValue = (lineBasic * gst) / 100
  const totalValue = lineBasic + gstValue
  return { lineBasic, gstValue, totalValue }
}

function calcMaterialLineTotal(basicPrice, quantity, gstPercent) {
  const { lineBasic, totalValue } = calcMaterialPricing(basicPrice, quantity, gstPercent)
  if (lineBasic <= 0) return ''
  return totalValue.toFixed(2)
}

function calcGrossTotalsFromRows(rows) {
  return rows.reduce(
    (acc, row) => {
      const { lineBasic, gstValue, totalValue } = calcMaterialPricing(
        row.basicPrice,
        row.quantity,
        row.gst
      )
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function formatTechnicalRemarksFromApi(record) {
  return String(
    record?.technicalRemarks ?? record?.technicalQueries ?? record?.technicalResponse ?? ''
  ).trim()
}

function mapMaterialFromApi(m) {
  const basicPrice =
    m.basicPrice != null && m.basicPrice !== '' && Number(m.basicPrice) !== 0
      ? String(m.basicPrice)
      : ''
  const quantity = m.quantity != null && m.quantity !== '' ? String(m.quantity) : ''
  const gst =
    m.gst != null && m.gst !== '' && Number(m.gst) !== 0
      ? String(m.gst)
      : m.gstPercent != null && m.gstPercent !== '' && Number(m.gstPercent) !== 0
        ? String(m.gstPercent)
        : ''
  const totalFromApi = m.totalValue != null && m.totalValue !== '' ? String(m.totalValue) : ''
  const totalValue = calcMaterialLineTotal(basicPrice, quantity, gst) || totalFromApi
  const gstValue =
    m.gstValue != null && m.gstValue !== ''
      ? String(m.gstValue)
      : calcMaterialPricing(basicPrice, quantity, gst).gstValue.toFixed(2)

  // Never fall back to m._id — that is the PO material line id, not a PR/SO/RFQ ref
  const referenceNumber_id = String(
    m.referenceNumber_id || m.pr_id || ''
  ).trim()

  return {
    materialCode: m.materialCode || '',
    materialDescription: m.materialDescription || '',
    hsn: m.hsn || m.hsnCode || '',
    uom: m.uom || '',
    quantity,
    referenceNumber: m.referenceNumber || m.prNumber || m.prNo || '',
    referenceNumber_id,
    customerOrderid: m.customerOrderid || m.customerOrderId || '',
    technicalRemarks: formatTechnicalRemarksFromApi(m),
    basicPrice,
    gst,
    gstValue: gstValue === '0.00' && !gst ? '' : gstValue,
    totalValue,
    sourceType: m.sourceType || ''
  }
}

function mapMilestoneFromApi(m) {
  const attachments = Array.isArray(m.attachments)
    ? m.attachments.map(mapMilestoneAttachmentFromApi).filter((att) => att.s3Key)
    : []
  const currentUser = getCurrentUserFromStorage()
  const responsibilityUserId =
    String(m.responsibilityUserId || '').trim() || currentUser.id
  const responsibilityUser =
    String(m.responsibilityUser || '').trim() ||
    (responsibilityUserId === currentUser.id ? currentUser.name : '')
  return {
    milestoneActivity: m.milestoneActivity || '',
    actionToBeTaken: m.actionToBeTaken || '',
    responsibilityUser,
    responsibilityUserId,
    attachments,
    pendingFiles: []
  }
}

function buildInitialTabSnapshots(po) {
  const terms =
    Array.isArray(po.terms) && po.terms.length > 0
      ? po.terms.map((t) => ({ term: t.term || '', response: t.response || '' }))
      : getDefaultTerms()
  const materials =
    Array.isArray(po.materials) && po.materials.length > 0
      ? po.materials.map(mapMaterialFromApi)
      : [emptyMaterial()]
  const milestones =
    Array.isArray(po.milestone) && po.milestone.length > 0
      ? po.milestone.map(mapMilestoneFromApi)
      : getDefaultMilestones()

  return {
    details: JSON.stringify({
      poDate: parseDateFromApi(po.poDate) || getTodayIsoDate(),
      poDeliveryDate: parseDateFromApi(po.poDeliveryDate),
      poCurrency: po.poCurrency || 'INR',
      poSummary: po.poSummary || '',
      terms: terms.map((t) => ({ term: t.term, response: t.response }))
    }),
    materials: JSON.stringify(materials),
    attachments: JSON.stringify({
      customEmailId: String(po.customEmailId || '').trim(),
      pendingNames: []
    }),
    supplierMail: JSON.stringify({
      supplierId: po.supplierId || '',
      supplierName: String(po.supplierName || '').trim(),
      supplierContacts: Array.isArray(po.supplierContacts)
        ? po.supplierContacts.map((c) => ({
            supplierMailId: String(c.supplierMailId || c.email || '').trim(),
            supplierName: String(c.supplierName || po.supplierName || '').trim(),
            supplierPhoneNumber: String(
              c.supplierPhoneNumber || c.phonenumber || ''
            ).trim()
          }))
        : extractSupplierContactEmails(po).map((email) => ({
            supplierMailId: email,
            supplierName: String(po.supplierName || '').trim(),
            supplierPhoneNumber: String(
              po.supplierMobile || po.phonenumber || po.phoneNumber || ''
            ).trim()
          })),
      approverId: String(po.approverId || po.approverDetails?.id || '')
    }),
    milestone: JSON.stringify(milestones.map(serializeMilestoneForSnapshot))
  }
}

function mapPoOwnerFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getPoOwnerList(po) {
  if (!po || typeof po !== 'object') return []
  if (Array.isArray(po.poOwner) && po.poOwner.length > 0) return po.poOwner
  return []
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return ''
  const size = Number(bytes)
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const isEmptyMaterialRow = (row) =>
  !String(row.materialCode || '').trim() &&
  !String(row.materialDescription || '').trim() &&
  !String(row.quantity || '').trim()

const getProcurementKey = (item, index) =>
  item._id || `${item.prNo || ''}|${item.materialCode || ''}|${index}`

function materialMatchesProcurement(material, procurement) {
  if (!material || isEmptyMaterialRow(material)) return false
  const matRefId = String(material.referenceNumber_id || '').trim()
  const procId = String(procurement._id || procurement.id || '').trim()
  if (matRefId && procId && matRefId === procId) return true
  const matRef = String(material.referenceNumber || '').trim()
  const procRef = String(procurement.prNo || procurement.prNumber || '').trim()
  const matCode = String(material.materialCode || '').trim()
  const procCode = String(procurement.materialCode || '').trim()
  return matRef && procRef && matRef === procRef && matCode && procCode && matCode === procCode
}

function isProcurementInMaterials(materials, procurement) {
  return materials.some((m) => materialMatchesProcurement(m, procurement))
}

const getRfqKey = (item, index) =>
  item._id || item.id || `${item.rfqNumber || item.rfqNo || ''}|${index}`

function materialMatchesRfq(material, rfq) {
  if (!material || isEmptyMaterialRow(material)) return false
  const rfqId = String(rfq._id || rfq.id || '').trim()
  const matRefId = String(material.referenceNumber_id || '').trim()
  if (rfqId && matRefId && rfqId === matRefId) return true
  const rfqNo = String(rfq.rfqNumber || rfq.rfqNo || '').trim()
  const matRef = String(material.referenceNumber || '').trim()
  return rfqNo && matRef && rfqNo.toLowerCase() === matRef.toLowerCase()
}

function isRfqInMaterials(materials, rfq) {
  return materials.some((m) => materialMatchesRfq(m, rfq))
}

function getProcurementQuotationStatus(item) {
  return String(item?.quotationStatus || '').trim()
}

function extractRfqsList(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (typeof dataBlock === 'string') {
    try {
      dataBlock = JSON.parse(dataBlock)
    } catch {
      return []
    }
  }
  if (!dataBlock || typeof dataBlock !== 'object') return []
  if (Array.isArray(dataBlock)) return dataBlock
  const list = dataBlock.rfqs || dataBlock.rfqList || dataBlock.items
  if (Array.isArray(list)) return list
  if (dataBlock._id || dataBlock.rfqNumber || dataBlock.rfqNo) return [dataBlock]
  return []
}

function extractSalesQuotesList(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (typeof dataBlock === 'string') {
    try {
      dataBlock = JSON.parse(dataBlock)
    } catch {
      return []
    }
  }
  if (!dataBlock || typeof dataBlock !== 'object') return []
  if (Array.isArray(dataBlock)) return dataBlock
  const list =
    dataBlock.salesQuotes ||
    dataBlock.quotes ||
    dataBlock.quoteList ||
    dataBlock.items
  if (Array.isArray(list)) return list
  if (dataBlock._id || dataBlock.quoteNumber) return [dataBlock]
  return []
}

function extractSalesOrdersList(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (typeof dataBlock === 'string') {
    try {
      dataBlock = JSON.parse(dataBlock)
    } catch {
      return []
    }
  }
  if (!dataBlock || typeof dataBlock !== 'object') return []
  if (Array.isArray(dataBlock)) return dataBlock
  const list = dataBlock.salesOrders || dataBlock.orders || dataBlock.items
  if (Array.isArray(list)) return list
  if (dataBlock._id || dataBlock.id || dataBlock.cpoNumber) return [dataBlock]
  return []
}

const getSqKey = (item, index) =>
  item._id || item.id || `${item.quoteNumber || ''}|${index}`

function materialMatchesSq(material, quote) {
  if (!material || isEmptyMaterialRow(material)) return false
  const quoteId = String(quote._id || quote.id || '').trim()
  const matRefId = String(material.referenceNumber_id || '').trim()
  if (quoteId && matRefId && quoteId === matRefId) return true
  const quoteNo = String(quote.quoteNumber || '').trim()
  const matRef = String(material.referenceNumber || '').trim()
  return quoteNo && matRef && quoteNo.toLowerCase() === matRef.toLowerCase()
}

function isSqInMaterials(materials, quote) {
  return materials.some((m) => materialMatchesSq(m, quote))
}

const getSoKey = (item, index) =>
  item._id || item.id || `${item.cpoNumber || ''}|${index}`

function soMaterialLineMatches(row, soMaterial) {
  if (!row || !soMaterial) return false
  const quantity =
    soMaterial.quantity != null && soMaterial.quantity !== ''
      ? String(soMaterial.quantity)
      : ''
  return (
    String(row.materialCode || '').trim() === String(soMaterial.materialCode || '').trim() &&
    String(row.materialDescription || '').trim() ===
      String(soMaterial.materialDescription || '').trim() &&
    String(row.hsn || '').trim() ===
      String(soMaterial.hsn || soMaterial.hsnCode || '').trim() &&
    String(row.uom || '').trim() === String(soMaterial.uom || '').trim() &&
    String(row.quantity || '').trim() === quantity
  )
}

function isSoInMaterials(materials, order) {
  const orderMaterials = Array.isArray(order?.materials)
    ? order.materials.filter(hasSearchableMaterialRow)
    : []
  if (orderMaterials.length === 0) return false
  return orderMaterials.every((line) => materials.some((m) => soMaterialLineMatches(m, line)))
}

function normalizeHsnValue(value) {
  const hsn = String(value || '').trim()
  if (!hsn || /^-+$/.test(hsn)) return '-'
  return hsn
}

function hasSearchableMaterialRow(material) {
  if (!material || typeof material !== 'object') return false
  if (
    material.remainingQuantity != null &&
    material.remainingQuantity !== '' &&
    Number(material.remainingQuantity) <= 0
  ) {
    return false
  }
  return Boolean(
    String(material.materialCode || '').trim() ||
      String(material.materialDescription || '').trim() ||
      (material.quantity != null && material.quantity !== '') ||
      (material.remainingQuantity != null && material.remainingQuantity !== '')
  )
}

function resolveMaterialQuantity(material) {
  if (material?.remainingQuantity != null && material.remainingQuantity !== '') {
    return String(material.remainingQuantity)
  }
  if (material?.quantity != null && material.quantity !== '') {
    return String(material.quantity)
  }
  return ''
}

function stripMaterialPricingFields(row) {
  return {
    ...row,
    basicPrice: '',
    gst: DEFAULT_PR_GST_PERCENT,
    gstValue: '',
    totalValue: ''
  }
}

function toPoMaterialRowWithoutPricing(row) {
  return syncMaterialRowPricing(stripMaterialPricingFields(row))
}

const MATERIAL_SEARCH_TYPES = {
  pr: 'pr',
  rfq: 'rfq',
  sq: 'sq',
  so: 'so'
}

const MATERIAL_SEARCH_DEFAULT_QUERIES = {
  [MATERIAL_SEARCH_TYPES.pr]: 'ITS-',
  [MATERIAL_SEARCH_TYPES.rfq]: 'RFQ-ITS-',
  [MATERIAL_SEARCH_TYPES.sq]: 'SQ-ITS-',
  [MATERIAL_SEARCH_TYPES.so]: ''
}

function getMaterialSearchDefaultQuery(searchType) {
  return MATERIAL_SEARCH_DEFAULT_QUERIES[searchType] ?? MATERIAL_SEARCH_DEFAULT_QUERIES.pr
}

function syncMaterialRowPricing(row) {
  const next = { ...row }
  const hasBasicPrice = next.basicPrice !== '' && !isNaN(parseFloat(next.basicPrice))
  const hasQuantity = next.quantity !== '' && !isNaN(parseFloat(next.quantity))
  const hasGst = next.gst !== '' && !isNaN(parseFloat(next.gst))

  if (hasBasicPrice && hasQuantity && hasGst) {
    const pricing = calcMaterialPricing(next.basicPrice, next.quantity, next.gst)
    next.gstValue = pricing.gstValue.toFixed(2)
    next.totalValue = calcMaterialLineTotal(next.basicPrice, next.quantity, next.gst)
  } else {
    next.gstValue = ''
    next.totalValue = ''
  }
  return next
}

const findNextMaterialWriteIndex = (rows) => {
  const emptyIndex = rows.findIndex(isEmptyMaterialRow)
  return emptyIndex === -1 ? rows.length : emptyIndex
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function resolveSupplierEmail(supplier) {
  if (!supplier || typeof supplier !== 'object') return ''
  return String(
    supplier.email ||
      supplier.supplierMailId ||
      supplier.supplierEmail ||
      supplier.mailId ||
      supplier.mailid ||
      supplier.emailId ||
      supplier.clientEmail ||
      ''
  ).trim()
}

function resolveSupplierMobile(supplier) {
  if (!supplier || typeof supplier !== 'object') return ''
  return String(
    supplier.supplierMobile ||
      supplier.phonenumber ||
      supplier.phoneNumber ||
      supplier.mobile ||
      supplier.mobileNumber ||
      supplier.contactNumber ||
      ''
  ).trim()
}

function buildContactsForDisplay(supplier) {
  if (!supplier || typeof supplier !== 'object') return []

  if (Array.isArray(supplier.contacts) && supplier.contacts.length > 0) {
    return supplier.contacts
      .map((c) => ({
        contactPerson: String(c.contactPerson || '').trim(),
        email: String(c.email || '').trim(),
        phoneNumber: String(
          c.phonenumber ||
            c.phoneNumber ||
            c.mobile ||
            c.supplierPhoneNumber ||
            ''
        ).trim()
      }))
      .filter((c) => c.email)
  }

  const email = resolveSupplierEmail(supplier)
  if (!email) return []
  return [
    {
      contactPerson: String(supplier.contactPerson || '').trim(),
      email,
      phoneNumber: resolveSupplierMobile(supplier)
    }
  ]
}

function extractSupplierContactEmails(po) {
  if (Array.isArray(po?.supplierContacts) && po.supplierContacts.length > 0) {
    return po.supplierContacts
      .map((c) => String(c.supplierMailId || c.email || '').trim())
      .filter(Boolean)
  }
  const legacy = String(
    po?.supplierMailId || po?.supplierEmail || po?.email || ''
  ).trim()
  if (!legacy) return []
  return legacy
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean)
}

function Pocreate() {
  const navigate = useNavigate()
  const { id: editId } = useParams()
  const isEditMode = Boolean(editId)
  const [activeTab, setActiveTab] = useState('details')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingPoNumber, setIsFetchingPoNumber] = useState(!editId)
  const [isLoadingPo, setIsLoadingPo] = useState(Boolean(editId))
  const [fieldErrors, setFieldErrors] = useState({})
  const [poId, setPoId] = useState(editId || null)
  const [poRecordStatus, setPoRecordStatus] = useState('')
  const [poWorkflowStatus, setPoWorkflowStatus] = useState('Draft')
  const [showPreviewStep, setShowPreviewStep] = useState(false)
  const [materialCatalog, setMaterialCatalog] = useState([])
  const [isMaterialCatalogLoading, setIsMaterialCatalogLoading] = useState(false)
  const [unitOptions, setUnitOptions] = useState([])
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)
  const [materialCodeSearchTerms, setMaterialCodeSearchTerms] = useState({})
  const [showMaterialCodeDropdown, setShowMaterialCodeDropdown] = useState({})
  const [selectedMaterialCodeIndex, setSelectedMaterialCodeIndex] = useState({})
  const [materialCodeDropdownPos, setMaterialCodeDropdownPos] = useState({})
  const materialCodeInputRefs = useRef({})
  const materialDescInputRefs = useRef({})               // ADD
  const [showMaterialDescDropdown, setShowMaterialDescDropdown] = useState({})   // ADD
  const [selectedMaterialDescIndex, setSelectedMaterialDescIndex] = useState({}) // ADD
  const [materialDescDropdownPos, setMaterialDescDropdownPos] = useState({})     // ADD

  const [poDetails, setPoDetails] = useState({
    poNumber: '',
    poDate: getTodayIsoDate(),
    poDeliveryDate: '',
    poCurrency: 'INR',
    poSummary: '',
    pdfLink: '',
    pdfDownloadUrl: ''
  })

  const [poUsers, setPoUsers] = useState([])
  const [approverUsers, setApproverUsers] = useState([])
  const [isPoUsersLoading, setIsPoUsersLoading] = useState(false)
  const tabSnapshotsRef = useRef({
    details: '',
    materials: '',
    attachments: '',
    supplierMail: '',
    milestone: ''
  })
  const [selectedApproverId, setSelectedApproverId] = useState('')
  const [isSendingForApproval, setIsSendingForApproval] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')

  const [terms, setTerms] = useState(getDefaultTerms)
  const [materials, setMaterials] = useState([emptyMaterial()])
  const [activeMaterialRowIndex, setActiveMaterialRowIndex] = useState(0)
  const [materialSearchType, setMaterialSearchType] = useState(MATERIAL_SEARCH_TYPES.pr)
  const [materialSearchQuery, setMaterialSearchQuery] = useState(
    getMaterialSearchDefaultQuery(MATERIAL_SEARCH_TYPES.pr)
  )
  const [materialSearchLoading, setMaterialSearchLoading] = useState(false)
  const [materialSearchResults, setMaterialSearchResults] = useState([])
  const [selectedMaterialSearchKeys, setSelectedMaterialSearchKeys] = useState(new Set())
  const [materialSearchResultsOpen, setMaterialSearchResultsOpen] = useState(false)
  const materialSearchDebounceRef = useRef(null)
  const materialSearchRequestIdRef = useRef(0)

  const [milestones, setMilestones] = useState(getDefaultMilestones)
  const [customEmailId, setCustomEmailId] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [supplierMailId, setSupplierMailId] = useState('')
  const [supplierMobile, setSupplierMobile] = useState('')
  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1)
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true)
  const [isLoadingSupplierMailId, setIsLoadingSupplierMailId] = useState(false)
  const [supplierContacts, setSupplierContacts] = useState([])
  const [selectedSupplierEmails, setSelectedSupplierEmails] = useState(new Set())
  const [showSupplierEmailDropdown, setShowSupplierEmailDropdown] = useState(false)
  const supplierEmailPanelRef = useRef(null)

  const [poOwnerAttachments, setPoOwnerAttachments] = useState([])
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const [isUploadingMilestoneAttachments, setIsUploadingMilestoneAttachments] = useState(false)
  const attachmentInputRef = useRef(null)
  const milestoneAttachmentInputRefs = useRef({})
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('')

  const isPoActive = String(poRecordStatus || '').toLowerCase() === FINAL_STATUS.toLowerCase()
  const canAccessPreview = isPoActive || showPreviewStep
  const hasPoPdf = Boolean(String(poDetails.pdfDownloadUrl || poDetails.pdfLink || '').trim())

  const fetchNextPoNumber = async () => {
    setIsFetchingPoNumber(true)
    try {
      const response = await axios.post(API_URL, { action: 'get-next-po-number' })
      const responseData = parseApiResponse(response.data)
      if (isApiSuccess(responseData) && responseData.data?.nextSequenceNumber) {
        setPoDetails((prev) => ({
          ...prev,
          poNumber: responseData.data.nextSequenceNumber
        }))
      } else {
        toast.error(responseData?.message || 'Failed to fetch PO number', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Fetch next PO number error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to fetch next PO number', { autoClose: 2000 })
    } finally {
      setIsFetchingPoNumber(false)
    }
  }

function isValidEmailListString(value) {
  const emails = String(value || '')
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean)

  if (emails.length === 0) return false
  return emails.every((email) => isValidEmailAddress(email))
}

  const loadPoForEdit = useCallback(async () => {
    if (!editId) return

    setIsLoadingPo(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-detail-by-id',
        poId: editId
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData) || !responseData?.data) {
        toast.error(responseData?.message || 'Failed to load PO details', { autoClose: 2000 })
        navigate('/po')
        return
      }

      const po = responseData.data
      setPoId(po._id || po.id || editId)
      setPoRecordStatus(po.status || '')
      setPoWorkflowStatus(po.poStatus || 'Draft')
      setIsFetchingPoNumber(false)
      setPoDetails({
        poNumber: po.poNumber || '',
        poDate: parseDateFromApi(po.poDate) || getTodayIsoDate(),
        poDeliveryDate: parseDateFromApi(po.poDeliveryDate),
        poCurrency: po.poCurrency || 'INR',
        poSummary: po.poSummary || '',
        pdfLink: po.pdfLink || '',
        pdfDownloadUrl: po.pdfDownloadUrl || ''
      })

      if (Array.isArray(po.terms) && po.terms.length > 0) {
        setTerms(po.terms.map((t) => ({ term: t.term || '', response: t.response || '' })))
      } else {
        setTerms(getDefaultTerms())
      }

      if (Array.isArray(po.materials) && po.materials.length > 0) {
        setMaterials(po.materials.map(mapMaterialFromApi))
      } else {
        setMaterials([emptyMaterial()])
      }

      if (Array.isArray(po.milestone) && po.milestone.length > 0) {
        setMilestones(po.milestone.map(mapMilestoneFromApi))
      } else {
        setMilestones(getDefaultMilestones())
      }

      const owners = getPoOwnerList(po)
      setPoOwnerAttachments(owners.map(mapPoOwnerFromApi))
      setPendingAttachmentFiles([])

      if (po.customEmailId) setCustomEmailId(po.customEmailId)
      if (po.supplierName) {
        setSupplierName(po.supplierName)
        setSupplierSearchTerm(po.supplierName)
      }
      if (po.supplierId) setSupplierId(po.supplierId)
      const loadedEmails = extractSupplierContactEmails(po)
      if (loadedEmails.length > 0) {
        setSupplierMailId(loadedEmails.join(','))
        setSelectedSupplierEmails(new Set(loadedEmails))
      }
      if (po.supplierMobile || po.phonenumber || po.phoneNumber) {
        setSupplierMobile(
          String(po.supplierMobile || po.phonenumber || po.phoneNumber || '').trim()
        )
      }
      if (Array.isArray(po.supplierContacts) && po.supplierContacts.length > 0) {
        setSupplierContacts(
          po.supplierContacts
            .map((c) => ({
              contactPerson: String(c.contactPerson || c.supplierName || '').trim(),
              email: String(c.supplierMailId || c.email || '').trim(),
              phoneNumber: String(c.supplierPhoneNumber || c.phonenumber || '').trim()
            }))
            .filter((c) => c.email)
        )
      }

      if (po.approverId || po.approverDetails?.id) {
        setSelectedApproverId(String(po.approverId || po.approverDetails?.id || ''))
      }

      if (
        String(po.status || '').toLowerCase() === FINAL_STATUS.toLowerCase() ||
        po.pdfDownloadUrl ||
        po.pdfLink
      ) {
        setShowPreviewStep(true)
      }

      tabSnapshotsRef.current = buildInitialTabSnapshots(po)
    } catch (error) {
      console.error('Load PO for edit error:', error)
      toast.error('Failed to load PO details', { autoClose: 2000 })
      navigate('/po')
    } finally {
      setIsLoadingPo(false)
    }
  }, [editId, navigate])

  useEffect(() => {
    if (editId) {
      loadPoForEdit()
    } else {
      setPoDetails((prev) => ({ ...prev, poDate: getTodayIsoDate() }))
      fetchNextPoNumber()
    }
  }, [editId, loadPoForEdit])

  const buildDetailsSnapshot = () =>
    JSON.stringify({
      poDate: poDetails.poDate,
      poDeliveryDate: poDetails.poDeliveryDate,
      poCurrency: poDetails.poCurrency,
      poSummary: poDetails.poSummary,
      terms: terms.map((t) => ({ term: t.term, response: t.response }))
    })

  const buildMaterialsSnapshot = () => JSON.stringify(materials)

  const buildAttachmentsSnapshot = () =>
    JSON.stringify({
      customEmailId: customEmailId.trim(),
      pendingNames: pendingAttachmentFiles.map((f) => f.name)
    })

  const buildSupplierMailSnapshot = () => {
    const selectedEmails = Array.from(selectedSupplierEmails)
      .map((e) => String(e || '').trim())
      .filter(Boolean)
    const emails =
      selectedEmails.length > 0
        ? selectedEmails
        : supplierMailId
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
    const nameForContacts = supplierName.trim()
    return JSON.stringify({
      supplierId,
      supplierName: nameForContacts,
      supplierContacts: emails.map((email) => {
        const match = supplierContacts.find(
          (c) =>
            String(c.email || '')
              .trim()
              .toLowerCase() === email.toLowerCase()
        )
        return {
          supplierMailId: email,
          supplierName: nameForContacts,
          supplierPhoneNumber: String(
            match?.phoneNumber || match?.phonenumber || ''
          ).trim()
        }
      }),
      approverId: selectedApproverId
    })
  }

  const buildMilestoneSnapshot = () =>
    JSON.stringify(milestones.map(serializeMilestoneForSnapshot))

  const hasMilestonePendingFiles = () =>
    milestones.some((milestone) => (milestone.pendingFiles || []).length > 0)

  const isTabDirty = (tabKey) => {
    const current = {
      details: buildDetailsSnapshot(),
      materials: buildMaterialsSnapshot(),
      attachments: buildAttachmentsSnapshot(),
      supplierMail: buildSupplierMailSnapshot(),
      milestone: buildMilestoneSnapshot()
    }[tabKey]
    return current !== tabSnapshotsRef.current[tabKey]
  }

  const markTabSaved = (tabKey) => {
    const current = {
      details: buildDetailsSnapshot(),
      materials: buildMaterialsSnapshot(),
      attachments: buildAttachmentsSnapshot(),
      supplierMail: buildSupplierMailSnapshot(),
      milestone: buildMilestoneSnapshot()
    }
    tabSnapshotsRef.current[tabKey] = current[tabKey]
  }

  useEffect(() => {
    let cancelled = false

    const fetchUsers = async () => {
      // PO form user picker — lookup only; do not require user_management moduleAccess.
      setIsPoUsersLoading(true)
      try {
        const response = await axios.post(API_URL, {
          action: 'get-users',
          page: 1,
          limit: 1000
        })
        const responseData = parseApiResponse(response.data)
        const usersList = extractUsersList(responseData)

        if (cancelled) return

        if (!isApiSuccess(responseData)) {
          toast.error(responseData?.message || 'Failed to load users', { autoClose: 2000 })
          setPoUsers([])
          setApproverUsers([])
          return
        }

        const activeUsers = usersList.filter(isActiveUser)
        setPoUsers(normalizePoUsers(activeUsers))
        setApproverUsers(normalizePoUsers(activeUsers.filter(isAdminUser)))
      } catch (error) {
        if (!cancelled) {
          console.error('Fetch users error:', error)
          toast.error('Failed to load users', { autoClose: 2000 })
          setPoUsers([])
          setApproverUsers([])
        }
      } finally {
        if (!cancelled) setIsPoUsersLoading(false)
      }
    }

    fetchUsers()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const fetchUserEmail = async () => {
      const userId = localStorage.getItem('id')
      if (!userId) return
      try {
        const response = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        console.error('Fetch user email error:', error)
      }
    }
    fetchUserEmail()
  }, [])

  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsSuppliersLoading(true)
      try {
        const response = await axios.post(API_URL, { action: 'get-active-suppliers' })
        const responseData = parseApiResponse(response.data)
        if (isApiSuccess(responseData) && Array.isArray(responseData.data)) {
          setSuppliers(responseData.data)
          setFilteredSuppliers(responseData.data)
        }
      } catch (error) {
        console.error('Fetch suppliers error:', error)
      } finally {
        setIsSuppliersLoading(false)
      }
    }
    fetchSuppliers()
  }, [])

  useEffect(() => {
    if (
      activeTab !== 'preview' ||
      !String(poDetails.pdfDownloadUrl || poDetails.pdfLink || '').trim()
    ) {
      setPdfPreviewUrl('')
      return undefined
    }
    const url = poDetails.pdfDownloadUrl || poDetails.pdfLink
    if (!url) return undefined
    // Do not append cache-busting params to pre-signed S3 URLs.
    // It invalidates AWS signatures and causes SignatureDoesNotMatch.
    setPdfPreviewUrl(url)
    return undefined
  }, [activeTab, poDetails.pdfLink, poDetails.pdfDownloadUrl])

  const getUserId = () => {
    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return null
    }
    return userId
  }

  const fieldControlClass = (fieldKey, extra = '') => {
    const invalid = fieldErrors[fieldKey] ? ' border-danger' : ''
    return `form-control${invalid}${extra ? ` ${extra}` : ''}`
  }

  const clearFieldError = (fieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev
      const next = { ...prev }
      delete next[fieldKey]
      return next
    })
  }

  const handleDetailsChange = (e) => {
    const { name, value } = e.target
    if (name === 'poNumber') return
    setPoDetails((prev) => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) clearFieldError(name)
  }

  const updateTerm = (index, field, value) => {
    setTerms((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const addTermRowAfter = (index) => {
    setTerms((prev) => {
      if (prev.length === 0) return [emptyTerm()]
      const next = [...prev]
      next.splice(index + 1, 0, emptyTerm())
      return next
    })
  }

  const removeTermRow = (index) => {
    setTerms((prev) => prev.filter((_, i) => i !== index))
  }

  const updateMaterial = (index, field, value) => {
    setMaterials((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, [field]: value }
        // Changing / clearing Ref. No must drop stale PR/SO/RFQ linkage ids
        if (field === 'referenceNumber') {
          const prevRef = String(row.referenceNumber || '').trim()
          const nextRef = String(value || '').trim()
          if (prevRef !== nextRef) {
            next.referenceNumber_id = ''
            if (!nextRef) {
              next.customerOrderid = ''
              if (row.sourceType && row.sourceType !== 'manual') {
                next.sourceType = 'manual'
              }
            }
          }
        }
        return syncMaterialRowPricing(next)
      })
    )
  }

  const handleMaterialDecimalChange = (index, field, value) => {
    if (!isDecimalInput(value)) return
    updateMaterial(index, field, value)
  }

const handleGstChange = (index, field, value) => {
  const updated = [...materials];

  updated[index][field] = value;

  const { gstValue, totalValue } = calcMaterialPricing(
    updated[index].basicPrice,
    updated[index].quantity,
    updated[index].gst
  );

  updated[index].gstValue = gstValue.toFixed(2);
  updated[index].totalValue = totalValue.toFixed(2);

  setMaterials(updated);
};

  const handleQuantityChange = (index, value) => {
    if (value === '' || isDecimalInput(value)) {
      updateMaterial(index, 'quantity', value)
    }
  }

  const addMaterialRowAfter = (index) => {
    setMaterials((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyMaterial())
      return next
    })
    setActiveMaterialRowIndex(index + 1)
  }

  const removeMaterialRow = (index) => {
    if (materials.length <= 1) {
      toast.error('At least one material row is required', { autoClose: 2000 })
      return
    }
    setMaterials((prev) => prev.filter((_, i) => i !== index))
  }

  const procurementToMaterialRow = (procurement) => {
    const quantity = resolveMaterialQuantity(procurement)
    // PR lines only need ref/code/desc/hsn/uom/qty — pricing optional; GST defaults to 18%
    return syncMaterialRowPricing({
      ...emptyMaterial(),
      materialCode: procurement.materialCode || '-',
      materialDescription: procurement.materialDescription || '',
      hsn: procurement.hsnCode || procurement.hsn || '-',
      uom: procurement.uom || '',
      quantity,
      referenceNumber: String(procurement.prNo || procurement.prNumber || '').trim(),
      referenceNumber_id: String(procurement._id || procurement.id || '').trim(),
      technicalRemarks: '',
      basicPrice: '',
      gst: DEFAULT_PR_GST_PERCENT,
      gstValue: '',
      totalValue: '',
      sourceType: 'pr'
    })
  }

  const rfqMaterialToPoRow = (material, rfq) => {
    const rfqNo = String(rfq.rfqNumber || rfq.rfqNo || '').trim()
    const rfqId = String(rfq._id || rfq.id || '').trim()
    const quantity = resolveMaterialQuantity(material)
    return toPoMaterialRowWithoutPricing({
      ...emptyMaterial(),
      materialCode: material.materialCode || '-',
      materialDescription: material.materialDescription || '',
      hsn: normalizeHsnValue(material.hsn || material.hsnCode),
      uom: material.uom || '',
      quantity,
      referenceNumber: rfqNo,
      referenceNumber_id: rfqId,
      technicalRemarks: formatTechnicalRemarksFromApi(material),
      sourceType: 'rfq'
    })
  }

  const sqMaterialToPoRow = (material, quote) => {
    const quoteNo = String(quote.quoteNumber || '').trim()
    const quoteId = String(quote._id || quote.id || '').trim()
    const quantity = resolveMaterialQuantity(material)
    return toPoMaterialRowWithoutPricing({
      ...emptyMaterial(),
      materialCode: material.materialCode || '-',
      materialDescription: material.materialDescription || '',
      hsn: normalizeHsnValue(material.hsn || material.hsnCode),
      uom: material.uom || '',
      quantity,
      referenceNumber: quoteNo,
      referenceNumber_id: quoteId,
      technicalRemarks: formatTechnicalRemarksFromApi(material),
      sourceType: 'sq'
    })
  }

  const soMaterialToPoRow = (material, order) => {
    const soNo = String(order?.cpoNumber || order?.cpoNo || '').trim()
    // Prefer SO line-item id so each material keeps its own referenceNumber_id
    const soLineId = String(
      material?._id || material?.id || order?._id || order?.id || ''
    ).trim()
    const quantity = resolveMaterialQuantity(material)
    return syncMaterialRowPricing({
      ...emptyMaterial(),
      materialCode: material.materialCode || '-',
      materialDescription: material.materialDescription || '',
      hsn: normalizeHsnValue(material.hsn || material.hsnCode),
      uom: material.uom || '',
      quantity,
      referenceNumber: soNo,
      referenceNumber_id: soLineId,
      customerOrderid: String(material.customerOrderid || material.customerOrderId || '').trim(),
      gst: DEFAULT_PR_GST_PERCENT,
      sourceType: 'so'
    })
  }

  const emptyRfqToPoRow = (rfq) => {
    const rfqNo = String(rfq.rfqNumber || rfq.rfqNo || '').trim()
    return toPoMaterialRowWithoutPricing({
      ...emptyMaterial(),
      materialCode: '-',
      hsn: '-',
      referenceNumber: rfqNo,
      referenceNumber_id: String(rfq._id || rfq.id || '').trim()
    })
  }

  const emptySqToPoRow = (quote) => {
    const quoteNo = String(quote.quoteNumber || '').trim()
    return toPoMaterialRowWithoutPricing({
      ...emptyMaterial(),
      materialCode: '-',
      hsn: '-',
      referenceNumber: quoteNo,
      referenceNumber_id: String(quote._id || quote.id || '').trim()
    })
  }

  const buildRowsFromSearchItem = (item) => {
    if (materialSearchType === MATERIAL_SEARCH_TYPES.rfq) {
      const rfqMaterials = Array.isArray(item.materials)
        ? item.materials.filter(hasSearchableMaterialRow)
        : []
      return rfqMaterials.length > 0
        ? rfqMaterials.map((m) => rfqMaterialToPoRow(m, item))
        : [emptyRfqToPoRow(item)]
    }
    if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) {
      const quoteMaterials = Array.isArray(item.materials)
        ? item.materials.filter(hasSearchableMaterialRow)
        : []
      return quoteMaterials.length > 0
        ? quoteMaterials.map((m) => sqMaterialToPoRow(m, item))
        : [emptySqToPoRow(item)]
    }
    if (materialSearchType === MATERIAL_SEARCH_TYPES.so) {
      const orderMaterials = Array.isArray(item.materials)
        ? item.materials.filter(hasSearchableMaterialRow)
        : []
      return orderMaterials.length > 0
        ? orderMaterials.map((m) => soMaterialToPoRow(m, item))
        : []
    }
    return [procurementToMaterialRow(item)]
  }

  const getMaterialSearchKey = (item, index) => {
    if (materialSearchType === MATERIAL_SEARCH_TYPES.rfq) return getRfqKey(item, index)
    if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) return getSqKey(item, index)
    if (materialSearchType === MATERIAL_SEARCH_TYPES.so) return getSoKey(item, index)
    return getProcurementKey(item, index)
  }

  const isMaterialSearchItemAdded = (item) => {
    if (materialSearchType === MATERIAL_SEARCH_TYPES.rfq) return isRfqInMaterials(materials, item)
    if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) return isSqInMaterials(materials, item)
    if (materialSearchType === MATERIAL_SEARCH_TYPES.so) return isSoInMaterials(materials, item)
    return isProcurementInMaterials(materials, item)
  }

  const getMaterialSearchEmptyMessage = (searchType) => {
    if (searchType === MATERIAL_SEARCH_TYPES.rfq) return 'Enter RFQ number to search'
    if (searchType === MATERIAL_SEARCH_TYPES.sq) return 'Enter SQ number to search'
    if (searchType === MATERIAL_SEARCH_TYPES.so) return 'Enter SO / CPO number to search'
    return 'Enter PR number to search'
  }

  const getMaterialSearchNotFoundMessage = (searchType) => {
    if (searchType === MATERIAL_SEARCH_TYPES.rfq) return 'No RFQs found'
    if (searchType === MATERIAL_SEARCH_TYPES.sq) return 'No sales quotes found'
    if (searchType === MATERIAL_SEARCH_TYPES.so) return 'No sales orders found'
    return 'No procurement records found'
  }

  const getMaterialSearchErrorMessage = (searchType) => {
    if (searchType === MATERIAL_SEARCH_TYPES.rfq) return 'Failed to search RFQs'
    if (searchType === MATERIAL_SEARCH_TYPES.sq) return 'Failed to search sales quotes'
    if (searchType === MATERIAL_SEARCH_TYPES.so) return 'Failed to search sales orders'
    return 'Failed to search procurement by PR number'
  }

  const searchMaterialItems = useCallback(
    async (searchTerm, options = {}) => {
      const { showToast = true, searchType = materialSearchType } = options
      const query = String(searchTerm ?? '').trim()

      if (!query) {
        setMaterialSearchResults([])
        setMaterialSearchResultsOpen(false)
        if (showToast) {
          toast.error(getMaterialSearchEmptyMessage(searchType), { autoClose: 2000 })
        }
        return
      }

      if (searchType === MATERIAL_SEARCH_TYPES.pr && query.length < 3) {
        setMaterialSearchResults([])
        setMaterialSearchResultsOpen(false)
        return
      }

      const requestId = ++materialSearchRequestIdRef.current
      setMaterialSearchLoading(true)

      try {
        let list = []
        if (searchType === MATERIAL_SEARCH_TYPES.rfq) {
          const response = await axios.post(API_URL, {
            action: 'get-all-rfqs-with-remaining',
            page: 1,
            limit: 5000,
            search: query,
            currentUserId: localStorage.getItem('id') || undefined
          })
          if (requestId !== materialSearchRequestIdRef.current) return
          const responseData = parseApiResponse(response.data)
          list = extractRfqsList(responseData)
          if (!isApiSuccess(responseData) && list.length === 0) {
            setMaterialSearchResults([])
            setMaterialSearchResultsOpen(false)
            if (showToast) {
              toast.error(responseData?.message || 'Failed to fetch RFQs', { autoClose: 2000 })
            }
            return
          }
        } else if (searchType === MATERIAL_SEARCH_TYPES.sq) {
          const response = await axios.post(API_URL, {
            action: 'get-all-sales-quotes-with-remaining',
            page: 1,
            limit: 5000,
            search: query,
            status: 'Active',
            currentUserId: localStorage.getItem('id') || undefined
          })
          if (requestId !== materialSearchRequestIdRef.current) return
          const responseData = parseApiResponse(response.data)
          list = extractSalesQuotesList(responseData)
          if (!isApiSuccess(responseData) && list.length === 0) {
            setMaterialSearchResults([])
            setMaterialSearchResultsOpen(false)
            if (showToast) {
              toast.error(responseData?.message || 'Failed to fetch sales quotes', {
                autoClose: 2000
              })
            }
            return
          }
        } else if (searchType === MATERIAL_SEARCH_TYPES.so) {
          const response = await axios.post(API_URL, {
            action: 'get-all-sales-orders-with-remaining',
            page: 1,
            limit: 5000,
            search: query,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            poCreated: false,
            status: 'active',
            currentUserId: localStorage.getItem('id') || undefined
          })
          if (requestId !== materialSearchRequestIdRef.current) return
          const responseData = parseApiResponse(response.data)
          list = extractSalesOrdersList(responseData)
          if (!isApiSuccess(responseData) && list.length === 0) {
            setMaterialSearchResults([])
            setMaterialSearchResultsOpen(false)
            if (showToast) {
              toast.error(responseData?.message || 'Failed to fetch sales orders', {
                autoClose: 2000
              })
            }
            return
          }
        } else {
          // LOOKUP_API_ACTIONS: PR picker — needs po access, not procurement_tracker
          const response = await axios.post(API_URL, {
            action: 'get-all-procurements-with-remaining',
            page: 1,
            limit: 5000,
            prNo: query,
            currentUserId: localStorage.getItem('id') || undefined
          })
          if (requestId !== materialSearchRequestIdRef.current) return
          const responseData = parseApiResponse(response.data)
          list = responseData?.data?.procurements || []
          if (!isApiSuccess(responseData)) {
            setMaterialSearchResults([])
            setMaterialSearchResultsOpen(false)
            if (showToast) {
              toast.error(responseData?.message || 'Failed to search procurements', {
                autoClose: 2000
              })
            }
            return
          }
        }

        if (Array.isArray(list) && list.length > 0) {
          setMaterialSearchResults(list)
          setMaterialSearchResultsOpen(true)
          setSelectedMaterialSearchKeys(new Set())
        } else {
          setMaterialSearchResults([])
          setMaterialSearchResultsOpen(false)
          if (showToast) {
            toast.info(getMaterialSearchNotFoundMessage(searchType), { autoClose: 2000 })
          }
        }
      } catch (error) {
        if (requestId !== materialSearchRequestIdRef.current) return
        console.error('Material search error:', error)
        toast.error(getMaterialSearchErrorMessage(searchType), { autoClose: 2000 })
      } finally {
        if (requestId === materialSearchRequestIdRef.current) {
          setMaterialSearchLoading(false)
        }
      }
    },
    [materialSearchType]
  )

  const handleMaterialSearchTypeChange = (e) => {
    const nextType = e.target.value
    setMaterialSearchType(nextType)
    setMaterialSearchQuery(getMaterialSearchDefaultQuery(nextType))
    setMaterialSearchResults([])
    setSelectedMaterialSearchKeys(new Set())
    setMaterialSearchResultsOpen(false)
    materialSearchRequestIdRef.current += 1
  }

  const handleMaterialSearchChange = (e) => {
    const value = e.target.value
    setMaterialSearchQuery(value)
    if (materialSearchDebounceRef.current) clearTimeout(materialSearchDebounceRef.current)
    materialSearchDebounceRef.current = setTimeout(() => {
      searchMaterialItems(value, { showToast: false, searchType: materialSearchType })
    }, 400)
  }

  const handleMaterialSearchClick = () =>
    searchMaterialItems(materialSearchQuery, { searchType: materialSearchType })

  const handleMaterialSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      searchMaterialItems(materialSearchQuery, { searchType: materialSearchType })
    }
  }

  const getSelectableMaterialSearchKeys = () =>
    materialSearchResults
      .map((item, index) => ({ item, key: getMaterialSearchKey(item, index) }))
      .filter(({ item }) => !isMaterialSearchItemAdded(item))
      .map(({ key }) => key)

  const countPendingMaterialSelection = () =>
    materialSearchResults.filter(
      (item, index) =>
        selectedMaterialSearchKeys.has(getMaterialSearchKey(item, index)) &&
        !isMaterialSearchItemAdded(item)
    ).length

  const countAddedInCurrentMaterialResults = () =>
    materialSearchResults.filter((item) => isMaterialSearchItemAdded(item)).length

  const toggleMaterialSearchSelection = (key) => {
    const item = materialSearchResults.find(
      (entry, index) => getMaterialSearchKey(entry, index) === key
    )
    if (item && isMaterialSearchItemAdded(item)) return
    setSelectedMaterialSearchKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleSelectAllMaterialSearchResults = () => {
    const selectable = getSelectableMaterialSearchKeys()
    if (selectable.length === 0) return
    const allSelected = selectable.every((key) => selectedMaterialSearchKeys.has(key))
    setSelectedMaterialSearchKeys((prev) => {
      const next = new Set(prev)
      if (allSelected) selectable.forEach((key) => next.delete(key))
      else selectable.forEach((key) => next.add(key))
      return next
    })
  }

  const applySelectedMaterialSearchResults = () => {
    const itemsToAdd = materialSearchResults
      .map((item, index) => ({ item, key: getMaterialSearchKey(item, index) }))
      .filter(
        ({ key, item }) =>
          selectedMaterialSearchKeys.has(key) && !isMaterialSearchItemAdded(item)
      )

    if (itemsToAdd.length === 0) {
      toast.error('Select new items that are not already added', { autoClose: 2000 })
      return
    }

    let addedRowCount = 0
    setMaterials((prev) => {
      const next = [...prev]
      let writeIndex = findNextMaterialWriteIndex(next)
      itemsToAdd.forEach(({ item }) => {
        const rowsToInsert = buildRowsFromSearchItem(item)
        rowsToInsert.forEach((rowData) => {
          const rowToInsert =
            materialSearchType === MATERIAL_SEARCH_TYPES.so
              ? rowData
              : materialSearchType === MATERIAL_SEARCH_TYPES.rfq ||
                  materialSearchType === MATERIAL_SEARCH_TYPES.sq
                ? toPoMaterialRowWithoutPricing(rowData)
                : rowData

          if (writeIndex < next.length) next[writeIndex] = rowToInsert
          else next.push(rowToInsert)
          writeIndex += 1
          addedRowCount += 1
        })
      })
      return next
    })

    setSelectedMaterialSearchKeys((prev) => {
      const next = new Set(prev)
      itemsToAdd.forEach(({ key }) => next.delete(key))
      return next
    })

    setMaterialSearchResultsOpen(false)
    toast.success(`${addedRowCount} material row(s) added`, { autoClose: 2000 })
  }

  const updateMilestone = (index, field, value) => {
    setMilestones((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, [field]: value }
        if (field === 'responsibilityUserId') {
          const user = poUsers.find((u) => u._id === value)
          next.responsibilityUser = user?.name || ''
        }
        return next
      })
    )
  }

  const addMilestoneRowAfter = (index) => {
    setMilestones((prev) => {
      const next = [...prev]
      next.splice(index + 1, 0, emptyMilestone())
      return next
    })
  }

  const removeMilestoneRow = (index) => {
    setMilestones((prev) => prev.filter((_, i) => i !== index))
    delete milestoneAttachmentInputRefs.current[index]
  }

  const getMilestoneAttachmentCount = (milestone) =>
    (milestone.attachments || []).length + (milestone.pendingFiles || []).length

  const handleMilestoneAttachmentFileSelect = (index, e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return

    const milestone = milestones[index]
    if (!milestone) return

    const currentCount = getMilestoneAttachmentCount(milestone)
    if (currentCount + files.length > MAX_PO_MILESTONE_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_PO_MILESTONE_ATTACHMENTS} attachments allowed per milestone`, {
        autoClose: 2000
      })
      return
    }

    setMilestones((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, pendingFiles: [...(row.pendingFiles || []), ...files] }
          : row
      )
    )
  }

  const removeMilestonePendingFile = (milestoneIndex, fileIndex) => {
    setMilestones((prev) =>
      prev.map((row, i) =>
        i === milestoneIndex
          ? {
              ...row,
              pendingFiles: (row.pendingFiles || []).filter((_, idx) => idx !== fileIndex)
            }
          : row
      )
    )
  }

  const removeMilestoneAttachment = (milestoneIndex, attachmentIndex) => {
    setMilestones((prev) =>
      prev.map((row, i) =>
        i === milestoneIndex
          ? {
              ...row,
              attachments: (row.attachments || []).filter((_, idx) => idx !== attachmentIndex)
            }
          : row
      )
    )
  }

  const downloadMilestoneAttachment = async (s3Key) => {
    if (!s3Key) return
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-milestone-attachment-download-presigned-url',
        s3Keys: [s3Key]
      })
      const responseData = parseApiResponse(response.data)
      const downloadUrl = responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error('Failed to download milestone attachment', { autoClose: 2000 })
    }
  }

  const uploadMilestonePendingFiles = async (milestoneRows) => {
    const poNumber = poDetails.poNumber.trim()
    if (!poNumber) {
      toast.error('PO number is required for milestone attachments', { autoClose: 2000 })
      return null
    }

    const nextMilestones = milestoneRows.map((row) => ({
      ...row,
      attachments: [...(row.attachments || [])],
      pendingFiles: [...(row.pendingFiles || [])]
    }))

    let uploadedAny = false
    setIsUploadingMilestoneAttachments(true)
    try {
      for (let index = 0; index < nextMilestones.length; index += 1) {
        const row = nextMilestones[index]
        const pendingFiles = row.pendingFiles || []
        if (pendingFiles.length === 0) continue

        const contentTypesForPresign = pendingFiles.map((file) =>
          resolveFileContentType(file)
        )
        const response = await axios.post(API_URL, {
          action: 'get-po-milestone-attachment-upload-presigned-url',
          poNumber,
          fileNames: pendingFiles.map((file) => file.name),
          contentTypes: contentTypesForPresign
        })
        const responseData = parseApiResponse(response.data)
        if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
          toast.error(responseData?.message || 'Failed to get milestone upload URLs', {
            autoClose: 2000
          })
          return null
        }

        const presignedFiles = responseData.files
        if (presignedFiles.length !== pendingFiles.length) {
          toast.error('Milestone upload URL count mismatch', { autoClose: 2000 })
          return null
        }

        // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
        for (let fileIndex = 0; fileIndex < pendingFiles.length; fileIndex += 1) {
          const fileMeta = presignedFiles[fileIndex]
          const file = pendingFiles[fileIndex]
          const contentType =
            fileMeta.contentType ||
            contentTypesForPresign[fileIndex] ||
            'application/octet-stream'
          await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
        }

        const uploadedAttachments = presignedFiles.map((fileMeta, fileIndex) => {
          const file = pendingFiles[fileIndex]
          return mapMilestoneAttachmentFromApi({
            s3Key: fileMeta.s3Key,
            url: fileMeta.url || fileMeta.fileUrl || '',
            originalFileName: fileMeta.originalFileName || fileMeta.fileName || file.name,
            contentType:
              fileMeta.contentType ||
              contentTypesForPresign[fileIndex] ||
              'application/octet-stream',
            uploadedAt: fileMeta.uploadedAt || new Date().toISOString()
          })
        })

        row.attachments = [...row.attachments, ...uploadedAttachments]
        row.pendingFiles = []
        uploadedAny = true
      }

      if (uploadedAny) {
        setMilestones(nextMilestones)
      }
      return nextMilestones
    } catch (error) {
      console.error('Upload milestone attachments error:', error)
      toast.error('Failed to upload milestone attachments', { autoClose: 2000 })
      return null
    } finally {
      setIsUploadingMilestoneAttachments(false)
    }
  }

  const fetchMaterialCatalog = useCallback(async () => {
  setIsMaterialCatalogLoading(true)
  try {
    const response = await axios.post(API_URL, {
      action: 'get-all-material',
      page: 1,
      limit: 5000
    })
    const responseData = parseApiResponse(response.data)
    if (isApiSuccess(responseData)) {
      setMaterialCatalog(responseData.data?.materials || [])
    } else {
      setMaterialCatalog([])
    }
  } catch (error) {
    console.error('Fetch material catalog error:', error)
    setMaterialCatalog([])
  } finally {
    setIsMaterialCatalogLoading(false)
  }
}, [])

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-units',
        page: 1,
        limit: 500,
        sortBy: 'unit',
        sortOrder: 'asc'
      })
      const responseData = parseApiResponse(response.data)
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : []
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || '',
          unit: String(item?.unit || '').trim(),
          status: String(item?.status || '').trim()
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === 'active'
        )
      setUnitOptions(isApiSuccess(responseData) ? activeUnits : [])
    } catch (error) {
      console.error('Fetch units error:', error)
      setUnitOptions([])
    } finally {
      setIsLoadingUnits(false)
    }
  }, [])

useEffect(() => {
  fetchMaterialCatalog()
  fetchUnits()
}, [fetchMaterialCatalog, fetchUnits])

useEffect(() => {
  setMaterialCodeSearchTerms((prev) => {
    const next = { ...prev }
    materials.forEach((row, index) => {
      next[index] = row.materialCode || ''
    })
    return next
  })
}, [materials])

const getFilteredMaterialCatalog = (rowIndex) => {
  const search = (materialCodeSearchTerms[rowIndex] || '').toLowerCase()
  return materialCatalog.filter(
    (m) =>
      m.materialCode?.toLowerCase().includes(search) ||
      m.productName?.toLowerCase().includes(search) ||
      m.materialDescription?.toLowerCase().includes(search)
  )
}


const openMaterialCodeDropdown = useCallback((rowIndex) => {
  if (isMaterialCatalogLoading) return
  const el = materialCodeInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialCodeDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialCodeDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialCatalogLoading])

const closeMaterialCodeDropdown = (rowIndex) => {
  setShowMaterialCodeDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialCodeIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}

const handleMaterialCodeSearchChange = (rowIndex, value) => {
  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: value }))
  setSelectedMaterialCodeIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialCodeDropdown(rowIndex)
  updateMaterial(rowIndex, 'materialCode', value)

}

const handleMaterialCodeSelect = (rowIndex, material) => {

  setMaterials((prev) =>
    prev.map((row, i) => {
      if (i !== rowIndex) return row
      // Catalog pick is a manual line — do not keep previous SO/PR referenceNumber_id
      return syncMaterialRowPricing({
        ...row,
        materialCode: material.materialCode || '',
        materialDescription:
          material.productName || material.materialDescription || row.materialDescription,
        hsn: material.hsnCode || material.hsn || row.hsn,
        uom: material.uom || row.uom,
        referenceNumber: '',
        referenceNumber_id: '',
        customerOrderid: '',
        sourceType: 'manual'
      })
    })
  )

  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode }))

  closeMaterialCodeDropdown(rowIndex)
}

const handleMaterialCodeKeyDown = (rowIndex, e) => {
  if (!showMaterialCodeDropdown[rowIndex]) return
  const list = getFilteredMaterialCatalog(rowIndex)
  const currentIndex = selectedMaterialCodeIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialCodeIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialCodeIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialCodeSelect(rowIndex, list[currentIndex])
    }
  }
}

useEffect(() => {
  const activeRowIndex = Object.keys(showMaterialCodeDropdown).find(
    (idx) => showMaterialCodeDropdown[idx]
  )
  if (activeRowIndex === undefined) return

  const reposition = () => openMaterialCodeDropdown(activeRowIndex)
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
  }
}, [showMaterialCodeDropdown, openMaterialCodeDropdown])

  const handleSupplierSearchChange = (e) => {
    const searchValue = e.target.value
    setSupplierSearchTerm(searchValue)
    setShowSupplierDropdown(true)
    setSelectedSupplierIndex(-1)
    if (searchValue.trim() === '') {
      setFilteredSuppliers(suppliers)
    } else {
      setFilteredSuppliers(
        suppliers.filter((s) =>
          String(s.clientname || '').toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    }
    if (supplierId) {
      setSupplierName('')
      setSupplierId('')
      setSupplierMailId('')
      setSupplierMobile('')
      setSupplierContacts([])
      setSelectedSupplierEmails(new Set())
      setShowSupplierEmailDropdown(false)
    }
  }

  const getFilteredMaterialDescCatalog = (rowIndex) => {
  const search = (materials[rowIndex]?.materialDescription || '').toLowerCase()
  return materialCatalog.filter((m) => {
    return (
      (m.productName || '').toLowerCase().includes(search) ||
      (m.categoryName || '').toLowerCase().includes(search) ||
      (m.subcategoryName || '').toLowerCase().includes(search) ||
      (m.partNumber || '').toLowerCase().includes(search) ||
      (m.makeBrandOem || '').toLowerCase().includes(search)
    )
  })
}

const openMaterialDescDropdown = useCallback((rowIndex) => {
  if (isMaterialCatalogLoading) return
  const el = materialDescInputRefs.current[rowIndex]
  if (el) {
    const rect = el.getBoundingClientRect()
    setMaterialDescDropdownPos((prev) => ({
      ...prev,
      [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
    }))
  }
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: true }))
}, [isMaterialCatalogLoading])

const closeMaterialDescDropdown = (rowIndex) => {
  setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: false }))
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
}

const handleMaterialDescSearchChange = (rowIndex, value) => {
  updateMaterial(rowIndex, 'materialDescription', value)
  setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
  openMaterialDescDropdown(rowIndex)
}

const handleMaterialDescSelect = (rowIndex, material) => {


  setMaterials((prev) =>
    prev.map((row, i) => {
      if (i !== rowIndex) return row
      // Catalog pick is a manual line — do not keep previous SO/PR referenceNumber_id
      return syncMaterialRowPricing({
        ...row,
        materialCode: material.materialCode || row.materialCode,
        materialDescription:
          material.productName || material.materialDescription || row.materialDescription,
        hsn: material.hsnCode || material.hsn || row.hsn,
        uom: material.uom || row.uom,
        referenceNumber: '',
        referenceNumber_id: '',
        customerOrderid: '',
        sourceType: 'manual'
      })
    })
  )

  setMaterialCodeSearchTerms((prev) => ({ ...prev, [rowIndex]: material.materialCode || '' }))

  closeMaterialDescDropdown(rowIndex)
}

const handleMaterialDescKeyDown = (rowIndex, e) => {
  if (!showMaterialDescDropdown[rowIndex]) return
  const list = getFilteredMaterialDescCatalog(rowIndex)
  const currentIndex = selectedMaterialDescIndex[rowIndex] ?? -1

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
    }))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setSelectedMaterialDescIndex((prev) => ({
      ...prev,
      [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
    }))
  } else if (e.key === 'Enter') {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault()
      handleMaterialDescSelect(rowIndex, list[currentIndex])
    }
  } else if (e.key === 'Escape') {
    closeMaterialDescDropdown(rowIndex)
  }
}

  const fetchSupplierContactById = async (id) => {
    if (!id) return { email: '', mobile: '', contacts: [] }
    try {
      setIsLoadingSupplierMailId(true)
      const response = await axios.post(API_URL, { action: 'get-supplier', id })
      const responseData = parseApiResponse(response.data)
      if (isApiSuccess(responseData) && responseData?.supplier) {
        return {
          email: resolveSupplierEmail(responseData.supplier),
          mobile: resolveSupplierMobile(responseData.supplier),
          contacts: buildContactsForDisplay(responseData.supplier)
        }
      }
      return { email: '', mobile: '', contacts: [] }
    } catch (error) {
      console.error('Fetch supplier by ID error:', error)
      return { email: '', mobile: '', contacts: [] }
    } finally {
      setIsLoadingSupplierMailId(false)
    }
  }

  const handleSupplierSelect = async (supplier) => {
    const pickedSupplierId = supplier._id || ''
    setSupplierSearchTerm(supplier.clientname || '')
    setSupplierName(supplier.clientname || '')
    setSupplierId(pickedSupplierId)

    // Do not auto-select first contact email — user picks manually from dropdown
    setSupplierMailId('')
    setSelectedSupplierEmails(new Set())

    const mobileFromList = resolveSupplierMobile(supplier)
    let contactsFromList = buildContactsForDisplay(supplier)

    if (mobileFromList) {
      setSupplierMobile(mobileFromList)
    }
    setSupplierContacts(contactsFromList)

    if ((!mobileFromList || contactsFromList.length === 0) && pickedSupplierId) {
      const contact = await fetchSupplierContactById(pickedSupplierId)
      if (!mobileFromList) setSupplierMobile(contact.mobile)
      if (contactsFromList.length === 0 && contact.contacts.length > 0) {
        contactsFromList = contact.contacts
        setSupplierContacts(contact.contacts)
      }
    } else if (!pickedSupplierId) {
      if (!mobileFromList) setSupplierMobile('')
      setSupplierContacts([])
    }

    setShowSupplierDropdown(false)
    setSelectedSupplierIndex(-1)
    // Open mail dropdown so user can choose contact(s) manually
    setShowSupplierEmailDropdown(contactsFromList.length > 0)
  }

const toggleSupplierContactEmail = (email) => {
  setSelectedSupplierEmails((prev) => {
    const next = new Set(prev)
    if (next.has(email)) next.delete(email)
    else next.add(email)
    setSupplierMailId(Array.from(next).join(',')) 
    return next
  })
}

  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedSupplierIndex((prev) =>
        prev < filteredSuppliers.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedSupplierIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedSupplierIndex >= 0) {
        handleSupplierSelect(filteredSuppliers[selectedSupplierIndex])
      } else if (filteredSuppliers.length === 1) {
        handleSupplierSelect(filteredSuppliers[0])
      }
    } else if (e.key === 'Escape') {
      setShowSupplierDropdown(false)
    }
  }

  const buildTermsPayload = () =>
    terms
      .filter((t) => t.term.trim())
      .map((t) => ({ term: t.term.trim(), response: (t.response || '').trim() }))

  const getFilledMaterials = useCallback(
    () =>
      materials.filter(
        (m) =>
          m.materialCode.trim() || m.materialDescription.trim() || m.quantity !== ''
      ),
    [materials]
  )

  const materialGrossTotals = useMemo(
    () => calcGrossTotalsFromRows(getFilledMaterials()),
    [getFilledMaterials]
  )

  const buildMaterialsPayload = () =>
    getFilledMaterials().map((m) => {
      const gstForCalc =
        m.gst !== '' && m.gst != null
          ? m.gst
          : DEFAULT_PR_GST_PERCENT
      const pricing = calcMaterialPricing(m.basicPrice, m.quantity, gstForCalc)
      const row = {
        materialCode: m.materialCode.trim(),
        materialDescription: m.materialDescription.trim(),
        hsn: m.hsn.trim(),
        uom: m.uom.trim(),
        quantity: parseFloat(m.quantity) || 0,
        basicPrice: parseFloat(m.basicPrice) || 0,
        grossbasicPrice: pricing.lineBasic,
        gstPercent: parseFloat(gstForCalc) || 0,
        gstValue: pricing.gstValue,
        totalValue: parseFloat(m.totalValue) || pricing.totalValue
      }
      const refNum = String(m.referenceNumber || '').trim()
      const refId = String(m.referenceNumber_id || '').trim()
      const sourceType = String(m.sourceType || '').trim()
      const isLinkedSource =
        sourceType === 'pr' ||
        sourceType === 'rfq' ||
        sourceType === 'sq' ||
        sourceType === 'so'
      // Manual rows: never send a leftover SO/PR referenceNumber_id
      if (refNum) row.referenceNumber = refNum
      if (refId && (isLinkedSource || (refNum && sourceType !== 'manual'))) {
        row.referenceNumber_id = refId
      }
      const customerOrderid = String(m.customerOrderid || '').trim()
      if (customerOrderid && (isLinkedSource || sourceType !== 'manual')) {
        row.customerOrderid = customerOrderid
      }
      const remarks = String(m.technicalRemarks || '').trim()
      if (remarks) row.technicalRemarks = remarks
      return row
    })

  const buildMilestonePayload = (milestoneRows = milestones) =>
    milestoneRows
      .filter(
        (m) =>
          m.milestoneActivity.trim() ||
          m.actionToBeTaken.trim() ||
          m.responsibilityUserId.trim() ||
          (m.attachments || []).length > 0
      )
      .map((m) => ({
        milestoneActivity: m.milestoneActivity.trim(),
        actionToBeTaken: m.actionToBeTaken.trim(),
        responsibilityUser: m.responsibilityUser.trim(),
        responsibilityUserId: m.responsibilityUserId.trim(),
        attachments: (m.attachments || [])
          .map((att) => ({
            s3Key: att.s3Key,
            url: att.url,
            originalFileName: att.originalFileName,
            contentType: att.contentType,
            uploadedAt: att.uploadedAt
          }))
          .filter((att) => att.s3Key)
      }))

  const validateDetails = () => {
    const errors = {}
    if (isFetchingPoNumber) {
      errors.poNumber = 'PO number is still loading'
    } else if (!poDetails.poNumber.trim()) {
      errors.poNumber = 'PO number is required'
    }
    if (!poDetails.poDate) errors.poDate = 'PO date is required'
    if (!poDetails.poDeliveryDate) {
      errors.poDeliveryDate = 'Delivery date is required'
    } else if (isDeliveryBeforePoDate(poDetails.poDate, poDetails.poDeliveryDate)) {
      errors.poDeliveryDate = 'Delivery date cannot be before PO date'
    }
    if (!poDetails.poSummary.trim()) errors.poSummary = 'PO summary is required'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateMaterials = () => {
    const filled = getFilledMaterials()
    if (filled.length === 0) {
      toast.error('Add at least one material line', { autoClose: 2000 })
      return false
    }
    for (let i = 0; i < filled.length; i++) {
      const row = filled[i]
      const idx = materials.indexOf(row) + 1
      const fromPr = isPrMaterialRow(row)
      if (!row.materialCode.trim()) {
        toast.error(`Row ${idx}: Material code is required`, { autoClose: 2000 })
        return false
      }
      if (!row.materialDescription.trim()) {
        toast.error(`Row ${idx}: Material description is required`, { autoClose: 2000 })
        return false
      }
      if (row.quantity === '' || isNaN(parseFloat(row.quantity))) {
        toast.error(`Row ${idx}: Valid quantity is required`, { autoClose: 2000 })
        return false
      }
      // PR-sourced rows: only ref/code/desc/hsn/uom/qty required; pricing optional
      if (!fromPr) {
        if (row.basicPrice === '' || isNaN(parseFloat(row.basicPrice))) {
          toast.error(`Row ${idx}: Basic price is required`, { autoClose: 2000 })
          return false
        }
        if (row.gst === '' || isNaN(parseFloat(row.gst))) {
          toast.error(`Row ${idx}: GST % is required`, { autoClose: 2000 })
          return false
        }
        const pricing = calcMaterialPricing(row.basicPrice, row.quantity, row.gst)
        const gstValue =
          row.gstValue !== '' && !isNaN(parseFloat(row.gstValue))
            ? parseFloat(row.gstValue)
            : pricing.gstValue
        if (row.gstValue === '' || isNaN(gstValue)) {
          toast.error(`Row ${idx}: GST value is required`, { autoClose: 2000 })
          return false
        }
        if (!String(row.technicalRemarks || '').trim()) {
          toast.error(`Row ${idx}: Technical remarks is required`, { autoClose: 2000 })
          return false
        }
      }
    }
    return true
  }

const validateAttachmentsTab = () => {
  if (customEmailId.trim() && !isValidEmailAddress(customEmailId)) {
    toast.error('Enter a valid custom email ID', { autoClose: 2000 })
    return false
  }
  if (supplierMailId.trim() && !isValidEmailListString(supplierMailId)) {
    toast.error('Supplier email ID is invalid', { autoClose: 2000 })
    return false
  }
  return true
}

const validateMilestones = () => {
  // Milestone tab is optional — empty or partial rows are allowed
  return true
}

const validateSupplierMailTab = () => {
  if (!supplierId || !supplierName.trim()) {
    toast.error('Please select a supplier before continuing', { autoClose: 2000 })
    return false
  }
  const selectedEmails = Array.from(selectedSupplierEmails).filter(Boolean)
  const mailList = selectedEmails.length
    ? selectedEmails
    : supplierMailId
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
  if (mailList.length === 0) {
    toast.error('Select at least one supplier email', { autoClose: 2000 })
    return false
  }
  if (!isValidEmailListString(mailList.join(','))) {
    toast.error('Supplier email ID is invalid', { autoClose: 2000 })
    return false
  }
  if (!validateAttachmentsTab()) return false
  if (!selectedApproverId) {
    toast.error('Select an approver before continuing', { autoClose: 2000 })
    return false
  }
  const approver = approverUsers.find((user) => user._id === selectedApproverId)
  if (!approver) {
    toast.error('Selected approver is invalid', { autoClose: 2000 })
    return false
  }
  return true
}

  const buildTab1Fields = () => {
    const nextPoStatus = poId
      ? resolvePoStatusForUpdate(poWorkflowStatus)
      : 'Draft'
    return {
      poNumber: poDetails.poNumber.trim(),
      poDate: formatDateForApi(poDetails.poDate),
      poDeliveryDate: formatDateForApi(poDetails.poDeliveryDate),
      poCurrency: poDetails.poCurrency.trim(),
      poSummary: poDetails.poSummary.trim(),
      status: poId ? (poRecordStatus || 'Draft') : 'Draft',
      poStatus: nextPoStatus,
      terms: buildTermsPayload()
    }
  }

  const applyPdfFieldsToPoDetails = (pdfFields) => {
    if (!pdfFields?.pdfLink && !pdfFields?.pdfDownloadUrl) return
    setPoDetails((prev) => {
      return {
        ...prev,
        ...(pdfFields.pdfLink ? { pdfLink: pdfFields.pdfLink } : {}),
        // If backend returned a fresh signed URL, use it.
        // If only pdfLink is returned, clear stale signed URL to avoid signature errors.
        pdfDownloadUrl:
          pdfFields.pdfDownloadUrl || (pdfFields.pdfLink ? '' : prev.pdfDownloadUrl)
      }
    })
    const preview = pdfFields.pdfDownloadUrl || pdfFields.pdfLink || ''
    if (preview) setPdfPreviewUrl(preview)
  }

  const refreshPoById = useCallback(async () => {
    if (!poId) return null
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-detail-by-id',
        poId
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData) || !responseData?.data) return null
      const po = responseData.data
      applyPdfFieldsToPoDetails({
        pdfLink: po.pdfLink || '',
        pdfDownloadUrl: po.pdfDownloadUrl || ''
      })
      if (po.status) setPoRecordStatus(po.status)
      if (po.poStatus) setPoWorkflowStatus(po.poStatus)
      return po
    } catch (error) {
      console.error('Refresh PO error:', error)
      return null
    }
  }, [poId])

  useEffect(() => {
    if (!showSupplierEmailDropdown) return undefined
    const onDocMouseDown = (e) => {
      if (
        supplierEmailPanelRef.current &&
        !supplierEmailPanelRef.current.contains(e.target)
      ) {
        setShowSupplierEmailDropdown(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [showSupplierEmailDropdown])

  // Always fetch latest PO links when opening Preview (signed URLs may expire quickly)
  useEffect(() => {
    if (activeTab !== 'preview' || !poId || !canAccessPreview) return undefined
    refreshPoById()
    return undefined
  }, [activeTab, poId, canAccessPreview, refreshPoById])

  // If PDF is still generating after activate/edit, poll detail until links appear
  useEffect(() => {
    if (activeTab !== 'preview' || !poId || !canAccessPreview) return undefined
    if (poDetails.pdfLink?.trim() || poDetails.pdfDownloadUrl?.trim()) return undefined

    let cancelled = false
    let attempts = 0
    const maxAttempts = 8

    const poll = async () => {
      if (cancelled || attempts >= maxAttempts) return
      attempts += 1
      const po = await refreshPoById()
      if (cancelled) return
      if (po?.pdfLink || po?.pdfDownloadUrl) return
      window.setTimeout(poll, 1500)
    }

    const timer = window.setTimeout(poll, 1200)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    activeTab,
    poId,
    canAccessPreview,
    poDetails.pdfLink,
    poDetails.pdfDownloadUrl,
    refreshPoById
  ])

  const applyPoFromResponse = (po) => {
    if (!po || typeof po !== 'object') return
    if (po.poNumber) setPoDetails((prev) => ({ ...prev, poNumber: po.poNumber }))
    if (po.status) setPoRecordStatus(po.status)
    if (po.poStatus) setPoWorkflowStatus(po.poStatus)
    if (po.approverId || po.approverDetails?.id) {
      setSelectedApproverId(String(po.approverId || po.approverDetails?.id || ''))
    } else if (po.approverName && approverUsers.length > 0) {
      const matched = approverUsers.find(
        (user) => user.name.toLowerCase() === String(po.approverName).toLowerCase()
      )
      if (matched?._id) setSelectedApproverId(matched._id)
    }
    applyPdfFieldsToPoDetails({
      pdfLink: po.pdfLink || '',
      pdfDownloadUrl: po.pdfDownloadUrl || ''
    })
    if (Array.isArray(po.materials) && po.materials.length > 0) {
      setMaterials(po.materials.map(mapMaterialFromApi))
    }
    if (Array.isArray(po.milestone) && po.milestone.length > 0) {
      setMilestones(po.milestone.map(mapMilestoneFromApi))
    }
    const owners = getPoOwnerList(po)
    if (owners.length > 0) setPoOwnerAttachments(owners.map(mapPoOwnerFromApi))
    if (po.customEmailId) setCustomEmailId(po.customEmailId)
    if (po.supplierName) {
      setSupplierName(po.supplierName)
      setSupplierSearchTerm(po.supplierName)
    }
    if (po.supplierId) setSupplierId(po.supplierId)
    const loadedEmails = extractSupplierContactEmails(po)
    if (loadedEmails.length > 0) {
      setSupplierMailId(loadedEmails.join(','))
      setSelectedSupplierEmails(new Set(loadedEmails))
    }
    if (po.supplierMobile || po.phonenumber || po.phoneNumber) {
      setSupplierMobile(
        String(po.supplierMobile || po.phonenumber || po.phoneNumber || '').trim()
      )
    }
    if (Array.isArray(po.supplierContacts) && po.supplierContacts.length > 0) {
      setSupplierContacts(
        po.supplierContacts
          .map((c) => ({
            contactPerson: String(c.contactPerson || c.supplierName || '').trim(),
            email: String(c.supplierMailId || c.email || '').trim(),
            phoneNumber: String(c.supplierPhoneNumber || c.phonenumber || '').trim()
          }))
          .filter((c) => c.email)
      )
    }
  }

  const postPoRequest = async (payload) => {
    const response = await axios.post(API_URL, payload)
    const responseData = parseApiResponse(response.data)
    if (!isApiSuccess(responseData)) {
      toast.error(responseData?.message || 'Failed to save PO', { autoClose: 2000 })
      return { ok: false, responseData }
    }
    return { ok: true, responseData }
  }

  const saveTab1Details = async () => {
    const userId = getUserId()
    if (!userId) return false
    setIsLoading(true)
    try {
      const wasApproved = isApprovedPoStatus(poWorkflowStatus)
      const tab1Fields = buildTab1Fields()
      let payload
      if (poId) {
        payload = {
          action: 'update-po',
          id: poId,
          updatedBy: userId,
          ...tab1Fields
        }
      } else {
        payload = {
          action: 'create-po',
          createdBy: userId,
          ...tab1Fields
        }
      }
      const { ok, responseData } = await postPoRequest(payload)
      if (!ok) return false
      const newId = poId || extractCreatedPoId(responseData)
      if (newId && !poId) setPoId(newId)
      applyPoFromResponse(responseData?.data)
      // Keep local workflow as Draft after editing a previously Approved PO
      if (wasApproved) {
        setPoWorkflowStatus('Draft')
      }
      toast.success(
        responseData?.message ||
          (wasApproved
            ? 'PO moved to Draft. Update details and send for approval again.'
            : 'Draft saved'),
        { autoClose: 2000 }
      )
      return true
    } catch (error) {
      console.error('Save PO details error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while saving PO', { autoClose: 2000 })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const saveTab2Materials = async () => {
    const userId = getUserId()
    if (!userId || !poId) {
      toast.error('Save PO details first', { autoClose: 2000 })
      return false
    }
    setIsLoading(true)
    try {
      const { ok, responseData } = await postPoRequest({
        action: 'update-po',
        id: poId,
        updatedBy: userId,
        materials: buildMaterialsPayload(),
        // CRITICAL: after save, sync PR/RFQ/SQ statuses for Active PO
        ...(isPoActive ? { syncLinkedStatuses: true } : {})
      })
      if (!ok) return false
      applyPoFromResponse(responseData?.data)
      toast.success(
        isPoActive
          ? 'Materials saved and linked statuses synced'
          : 'Materials saved',
        { autoClose: 1500 }
      )
      return true
    } catch (error) {
      console.error('Save materials error:', error)
      toast.error('Failed to save materials', { autoClose: 2000 })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const persistPendingAttachments = async () => {
    if (pendingAttachmentFiles.length === 0) return true
    const userId = getUserId()
    if (!userId || !poId) return false
    const poNumber = poDetails.poNumber.trim()
    if (!poNumber) {
      toast.error('PO number is required for attachments', { autoClose: 2000 })
      return false
    }
    setIsUploadingAttachments(true)
    try {
      const fileNames = pendingAttachmentFiles.map((f) => f.name)
      const contentTypesForPresign = pendingAttachmentFiles.map((f) =>
        resolveFileContentType(f)
      )
      const response = await axios.post(API_URL, {
        action: 'get-po-owner-attachment-upload-presigned-url',
        poNumber,
        fileNames,
        contentTypes: contentTypesForPresign,
        expiresIn: 7200
      })
      const responseData = parseApiResponse(response.data)
      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        toast.error(responseData?.message || 'Failed to get upload URLs', { autoClose: 2000 })
        return false
      }
      const presignedFiles = responseData.files
      if (presignedFiles.length !== pendingAttachmentFiles.length) {
        toast.error('Upload URL count mismatch', { autoClose: 2000 })
        return false
      }

      // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
      for (let idx = 0; idx < pendingAttachmentFiles.length; idx += 1) {
        const fileMeta = presignedFiles[idx]
        const file = pendingAttachmentFiles[idx]
        const contentType =
          fileMeta.contentType ||
          contentTypesForPresign[idx] ||
          'application/octet-stream'
        await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
      }

      const updatePayload = {
        action: 'update-po',
        id: poId,
        updatedBy: userId,
        s3Keys: presignedFiles.map((f) => f.s3Key),
        originalFileNames: presignedFiles.map(
          (f, idx) => f.originalFileName || f.fileName || pendingAttachmentFiles[idx].name
        ),
        contentTypes: presignedFiles.map(
          (f, idx) =>
            f.contentType || contentTypesForPresign[idx] || 'application/octet-stream'
        )
      }
      if (poOwnerAttachments.length > 0) updatePayload.appendPoOwner = true
      if (customEmailId.trim()) updatePayload.customEmailId = customEmailId.trim()

      const { ok, responseData: updateData } = await postPoRequest(updatePayload)
      if (!ok) return false
      const owners = getPoOwnerList(updateData?.data)
      if (owners.length > 0) setPoOwnerAttachments(owners.map(mapPoOwnerFromApi))
      setPendingAttachmentFiles([])
      return true
    } catch (error) {
      console.error('Upload attachments error:', error)
      toast.error('Failed to upload attachments', { autoClose: 2000 })
      return false
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const saveTabAttachments = async () => {
    const userId = getUserId()
    if (!userId || !poId) {
      toast.error('Save PO details first', { autoClose: 2000 })
      return false
    }
    if (!validateAttachmentsTab()) return false
    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
      } else if (customEmailId.trim()) {
        const { ok } = await postPoRequest({
          action: 'update-po',
          id: poId,
          updatedBy: userId,
          ...(customEmailId.trim() ? { customEmailId: customEmailId.trim() } : {})
        })
        if (!ok) return false
      }
      toast.success('Attachments saved', { autoClose: 1500 })
      return true
    } finally {
      setIsLoading(false)
    }
  }

 const saveTabSupplierMail = async () => {
  const userId = getUserId()
  if (!userId || !poId) {
    toast.error('Save PO details first', { autoClose: 2000 })
    return false
  }

  setIsLoading(true)

  try {
    const approver = approverUsers.find(
      (user) => user._id === selectedApproverId
    )

    const selectedEmails = Array.from(selectedSupplierEmails)
      .map((e) => String(e || '').trim())
      .filter(Boolean)

    const emailsToSave =
      selectedEmails.length > 0
        ? selectedEmails
        : supplierMailId
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)

    const nameForContacts = String(supplierName || '').trim()
    const supplierContactsPayload = emailsToSave.map((email) => {
      const match = supplierContacts.find(
        (c) =>
          String(c.email || '')
            .trim()
            .toLowerCase() === email.toLowerCase()
      )
      return {
        supplierMailId: email,
        supplierName: nameForContacts,
        supplierPhoneNumber: String(
          match?.phoneNumber ||
            match?.phonenumber ||
            (emailsToSave.length === 1 ? supplierMobile : '') ||
            ''
        ).trim()
      }
    })

    const payload = {
      action: 'update-po',
      id: poId,
      updatedBy: userId,
      ...(supplierId ? { supplierId } : {}),
      supplierContacts: supplierContactsPayload,
      ...(approver
        ? {
            approverId: approver._id,
            approverName: approver.name
          }
        : {})
    }

    const { ok, responseData } = await postPoRequest(payload)

    if (!ok) return false

    applyPoFromResponse(responseData?.data)

    toast.success('Supplier details saved', {
      autoClose: 1500
    })

    return true

  } catch (error) {
    console.error('Save supplier mail error:', error)
    toast.error('Failed to save supplier details')
    return false
  } finally {
    setIsLoading(false)
  }
}

  const saveTabMilestones = async () => {
  const userId = getUserId()

  if (!userId || !poId) {
    toast.error('Save PO details first', { autoClose: 2000 })
    return false
  }

  setIsLoading(true)

  try {
    let milestoneRowsForSave = milestones

    if (hasMilestonePendingFiles()) {
      const uploadedMilestones = await uploadMilestonePendingFiles(milestones)

      if (!uploadedMilestones) return false

      milestoneRowsForSave = uploadedMilestones
    }

    const { ok, responseData } = await postPoRequest({
      action: 'update-po',
      id: poId,
      updatedBy: userId,
      milestone: buildMilestonePayload(milestoneRowsForSave)
    })

    if (!ok) return false

    applyPoFromResponse(responseData?.data)

    markTabSaved('milestone')

    toast.success('Milestones saved', { autoClose: 1500 })

    return true
  } catch (error) {
    console.error('Save milestone error:', error)
    toast.error('Failed to save milestones', { autoClose: 2000 })
    return false
  } finally {
    setIsLoading(false)
  }
}

  const activatePo = async () => {
    const userId = getUserId()
    if (!userId || !poId) {
      toast.error('Save PO details first', { autoClose: 2000 })
      return false
    }

    if (isTabDirty('materials')) {
      if (!validateMaterials()) return false
      const materialsSaved = await saveTab2Materials()
      if (!materialsSaved) return false
      markTabSaved('materials')
    }

    const wasInactive = !isPoActive
    setIsLoading(true)
    try {
      if (pendingAttachmentFiles.length > 0) {
        const uploaded = await persistPendingAttachments()
        if (!uploaded) return false
      }

      let milestoneRowsForSave = milestones
      if (hasMilestonePendingFiles()) {
        const uploadedMilestones = await uploadMilestonePendingFiles(milestones)
        if (!uploadedMilestones) return false
        milestoneRowsForSave = uploadedMilestones
      }

      // Clear stale preview so UI waits for regenerated PDF
      setPdfPreviewUrl('')

      const payload = {
        action: 'update-po',
        id: poId,
        updatedBy: userId,
        milestone: buildMilestonePayload(milestoneRowsForSave),
        // Rebuild / overwrite PDF from latest PO values (create or edit)
        regeneratePdf: true,
        status: FINAL_STATUS
      }

      const { ok, responseData } = await postPoRequest(payload)
      if (!ok) return false

      setPoRecordStatus(FINAL_STATUS)
      setShowPreviewStep(true)

      if (wasInactive) {
        // Silent retry so PR / RFQ / SQ quotation statuses re-sync after activation
        try {
          await postPoRequest({
            action: 'update-po',
            id: poId,
            updatedBy: userId,
            syncLinkedStatuses: true
          })
        } catch (syncError) {
          console.error('Silent linked status sync after activation failed:', syncError)
        }
      }

      markTabSaved('milestone')
      applyPoFromResponse(responseData?.data)

      const pdfFields = extractPdfFieldsFromPoResponse(responseData)
      applyPdfFieldsToPoDetails(pdfFields)
      if (!pdfFields.pdfLink && !pdfFields.pdfDownloadUrl) {
        // Backend may finish PDF write async — refresh detail for latest links
        await refreshPoById()
      }

      setActiveTab('preview')
      toast.success(
        responseData?.message ||
          (wasInactive
            ? 'PO activated. Review PDF and send for approval.'
            : 'PO updated. PDF regenerated with latest values.'),
        { autoClose: 2000 }
      )
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return true
    } catch (error) {
      console.error('Activate PO error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to activate PO', { autoClose: 2000 })
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (
      !validateDetails() ||
      !validateMaterials() ||
      !validateAttachmentsTab() ||
      !validateSupplierMailTab()
    ) {
      return
    }

    const supplierSaved = await saveTabSupplierMail()
    if (!supplierSaved) return

    await activatePo()
  }

  const sendPoForApproval = async () => {
    const userId = getUserId()
    if (!userId || !poId) {
      toast.error('Save PO details first', { autoClose: 2000 })
      return
    }
    if (!selectedApproverId) {
      toast.error('Select an approver before sending for approval', { autoClose: 2000 })
      return
    }
    const approver = approverUsers.find((user) => user._id === selectedApproverId)
    if (!approver) {
      toast.error('Selected approver is invalid', { autoClose: 2000 })
      return
    }
    const approverEmail = String(approver.email || '').trim()
    if (!approverEmail) {
      toast.error('Approver email is missing', { autoClose: 2000 })
      return
    }
    if (!poDetails.pdfLink?.trim() && !poDetails.pdfDownloadUrl?.trim()) {
      toast.error('PDF preview is not available yet. Please try again shortly.', {
        autoClose: 2000
      })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }

    setIsSendingForApproval(true)
    try {
      const { ok, responseData } = await postPoRequest({
        action: 'update-po',
        id: poId,
        updatedBy: userId,
        approverId: approver._id,
        approverName: approver.name,
        poStatus: 'Waiting for approval'
      })
      if (!ok) return

      setPoWorkflowStatus('Waiting for approval')
      applyPoFromResponse(responseData?.data)

      const emailResponse = await axios.post(API_URL, {
        action: 'send-po-email',
        poId,
        userid: userId,
        toEmails: [approverEmail],
        ccEmails,
        subject: 'PO Approval Required',
        content: 'Please review and approve.'
      })
      const emailData = parseApiResponse(emailResponse.data)
      if (!isApiSuccess(emailData)) {
        toast.error(
          emailData?.message || 'PO updated but failed to send approval email',
          { autoClose: 2000 }
        )
        return
      }

      const failCount = Number(emailData?.data?.failCount) || 0
      if (failCount > 0) {
        toast.error(emailData?.message || 'Failed to send approval email', { autoClose: 2000 })
        return
      }

      if (emailData?.data?.pdfLink) {
        setPoDetails((prev) => ({
          ...prev,
          pdfLink: emailData.data.pdfLink || prev.pdfLink
        }))
      }

      toast.success(emailData?.message || 'PO sent for approval', { autoClose: 2000 })
    } catch (error) {
      console.error('Send PO for approval error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'Failed to send PO for approval', { autoClose: 2000 })
    } finally {
      setIsSendingForApproval(false)
    }
  }

  const handleAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return
    const total = poOwnerAttachments.length + pendingAttachmentFiles.length + files.length
    if (total > MAX_PO_OWNER_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_PO_OWNER_ATTACHMENTS} attachments allowed`, { autoClose: 2000 })
      return
    }
    setPendingAttachmentFiles((prev) => [...prev, ...files])
  }

  const removePendingAttachment = (index) => {
    setPendingAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const downloadPoOwnerAttachment = async (s3Key) => {
    if (!s3Key) return
    try {
      const response = await axios.post(API_URL, {
        action: 'get-po-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = parseApiResponse(response.data)
      const downloadUrl = responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error('Failed to download attachment', { autoClose: 2000 })
    }
  }

  const handleBack = async () => {

  switch(activeTab){

    case 'details':
      await saveTab1Details();
      break;

    case 'materials':
      await saveTab2Materials();
      break;

    case 'attachments':
      await saveTabAttachments();
      break;

    case 'supplierMail':
      await saveTabSupplierMail();
      break;

    case 'milestone':
      await saveTabMilestones();
      break;

    default:
      break;
  }

  navigate('/po');
};

  const handleTabClick = (tab) => {
    if (tab === 'preview') {
      if (canAccessPreview) setActiveTab('preview')
      return
    }
    if (isEditMode) {
      setActiveTab(tab)
      return
    }
    if (tab === 'details') {
      setActiveTab('details')
      return
    }
    goToTab(tab)
  }

const TAB_ORDER = [
  'details',
  'materials',
  'attachments',
  'milestone',
  'supplierMail',
  'preview'
];

const isForwardMove = (fromTab, toTab) =>
  TAB_ORDER.indexOf(toTab) > TAB_ORDER.indexOf(fromTab);


const goToTab = async (nextTab) => {
  if (nextTab === activeTab) return;
  if (nextTab === 'preview' && !canAccessPreview) return;

  const forward = isForwardMove(activeTab, nextTab);

  if (forward) {
    switch (activeTab) {
      case 'details':
        if (!validateDetails()) return;
        if (!(await saveTab1Details())) return;
        break;

      case 'materials':
        if (!validateMaterials()) return;
        if (!(await saveTab2Materials())) return;
        break;

      case 'attachments':
        if (!(await saveTabAttachments())) return;
        break;

      case 'milestone':
        if (!validateMilestones()) return;
        if (!(await saveTabMilestones())) return;
        break;

      case 'supplierMail':
        if (!validateSupplierMailTab()) return;      
        if (!(await saveTabSupplierMail())) return;
        break;

      default:
        break;
    }
  }

  setActiveTab(nextTab);
};
  const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">
                {isEditMode ? 'Edit Purchase Order' : 'Create Purchase Order'}
              </h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <button
                     type="button"
                      className="btn btn-link p-0"
                       onClick={handleBack}>
                        <i className="fa-solid fa-arrow-left-long" /> Back
                    </button>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <button type="button" className={tabClass('details')} onClick={() => handleTabClick('details')}>
                        PO Details
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={tabClass('materials')} onClick={() => handleTabClick('materials')}>
                        Materials
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={tabClass('attachments')} onClick={() => handleTabClick('attachments')}>
                        Attachments
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={tabClass('milestone')} onClick={() => handleTabClick('milestone')}>
                        Milestone
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={tabClass('supplierMail')} onClick={() => handleTabClick('supplierMail')}>
                        Supplier Mail
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass('preview')}
                        onClick={() => handleTabClick('preview')}
                        disabled={!canAccessPreview}
                        style={!canAccessPreview ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      >
                        Preview
                      </button>
                    </li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit}>
                  {isLoadingPo ? (
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : (
                  <>
                  <div className="card-body">
                    {activeTab === 'details' && (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.poNumber ? 'text-danger' : ''}>PO Number</label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                name="poNumber"
                                className={fieldControlClass('poNumber')}
                                value={poDetails.poNumber}
                                readOnly
                                disabled={isFetchingPoNumber}
                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                              />
                              {isFetchingPoNumber && (
                                <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                                  <PulseLoader color="#1199B2" size={6} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.poDate ? 'text-danger' : ''}>PO Date</label>
                            <input
                              type="date"
                              name="poDate"
                              className={fieldControlClass('poDate')}
                              value={poDetails.poDate}
                              onChange={handleDetailsChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label className={fieldErrors.poDeliveryDate ? 'text-danger' : ''}>Delivery Date</label>
                            <input
                              type="date"
                              name="poDeliveryDate"
                              className={fieldControlClass('poDeliveryDate')}
                              value={poDetails.poDeliveryDate}
                              onChange={handleDetailsChange}
                              min={poDetails.poDate || undefined}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Currency</label>
                            <select
                              name="poCurrency"
                              className="form-control"
                              value={poDetails.poCurrency}
                              onChange={handleDetailsChange}
                            >
                              {CURRENCY_OPTIONS.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group">
                            <label className={fieldErrors.poSummary ? 'text-danger' : ''}>PO Summary</label>
                            <textarea
                              name="poSummary"
                              className={fieldControlClass('poSummary')}
                              rows={4}
                              value={poDetails.poSummary}
                              onChange={handleDetailsChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-12 mt-3">
                          <label className="mb-2">Terms &amp; Conditions</label>
                          <div style={{ overflowX: 'auto' }}>
                            <table
                              className="table table-bordered table-hover mb-0"
                              style={{ tableLayout: 'fixed', width: '100%' }}
                            >
                              <colgroup>
                                <col style={{ width: '56px' }} />
                                <col style={{ width: '28%' }} />
                                <col style={{ width: 'calc(72% - 146px)' }} />
                                <col style={{ width: '90px' }} />
                              </colgroup>
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>Term</th>
                                  <th style={{ fontSize: '12px' }}>Conditions</th>
                                  <th style={{ fontSize: '12px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {terms.map((row, index) => (
                                  <tr key={index}>
                                    <td style={{ verticalAlign: 'top' }}>{index + 1}</td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.term}
                                        onChange={(e) => updateTerm(index, 'term', e.target.value)}
                                        style={{ width: '100%' }}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        value={row.response}
                                        onChange={(e) => updateTerm(index, 'response', e.target.value)}
                                        style={{
                                          width: '100%',
                                          resize: 'vertical',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      />
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => addTermRowAfter(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-plus" aria-hidden="true" />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => removeTermRow(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'materials' && (
                      <div>
                        <div className="row align-items-end mb-3">
                          <div className="col-12">
                            <div className="form-group mb-0">
                              <label>Search by</label>
                              <div className="input-group">
                                <div className="input-group-prepend">
                                  <select
                                    className="form-control"
                                    value={materialSearchType}
                                    onChange={handleMaterialSearchTypeChange}
                                    style={{ maxWidth: '130px', flex: '0 0 130px' }}
                                  >
                                    <option value={MATERIAL_SEARCH_TYPES.pr}>PR</option>
                                    <option value={MATERIAL_SEARCH_TYPES.rfq}>RFQ</option>
                                    <option value={MATERIAL_SEARCH_TYPES.sq}>SQ</option>
                                    <option value={MATERIAL_SEARCH_TYPES.so}>SO</option>
                                  </select>
                                </div>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={
                                    materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                      ? 'Enter RFQ number...'
                                      : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                        ? 'Enter SQ number...'
                                        : materialSearchType === MATERIAL_SEARCH_TYPES.so
                                          ? 'Enter SO / CPO number...'
                                          : 'Enter PR number...'
                                  }
                                  value={materialSearchQuery}
                                  onChange={handleMaterialSearchChange}
                                  onKeyPress={handleMaterialSearchKeyPress}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-blue"
                                    onClick={handleMaterialSearchClick}
                                    disabled={materialSearchLoading}
                                  >
                                    {materialSearchLoading ? (
                                      <PulseLoader color="#fff" size={6} />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {materialSearchResults.length > 0 && (
                          <div
                            className="mb-3"
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              className="d-flex justify-content-between align-items-center flex-wrap"
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                gap: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => setMaterialSearchResultsOpen((open) => !open)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setMaterialSearchResultsOpen((open) => !open)
                                }
                              }}
                            >
                              <span
                                className="d-flex align-items-center"
                                style={{ fontWeight: 600, fontSize: '13px', gap: '8px' }}
                              >
                                <i
                                  className={`fa-solid ${
                                    materialSearchResultsOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                                  }`}
                                  style={{ width: '14px', color: '#003366' }}
                                  aria-hidden
                                />
                                {materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                  ? 'RFQ search results'
                                  : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                    ? 'SQ search results'
                                    : materialSearchType === MATERIAL_SEARCH_TYPES.so
                                      ? 'SO search results'
                                      : 'PR search results'}{' '}
                                — {countPendingMaterialSelection()} to add,{' '}
                                {countAddedInCurrentMaterialResults()} already added
                              </span>
                              {materialSearchResultsOpen && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-blue"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    applySelectedMaterialSearchResults()
                                  }}
                                  disabled={countPendingMaterialSelection() === 0}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                            {materialSearchResultsOpen && (
                              <div style={{ maxHeight: '220px', overflow: 'auto' }}>
                                {materialSearchType === MATERIAL_SEARCH_TYPES.rfq ||
                                materialSearchType === MATERIAL_SEARCH_TYPES.sq ||
                                materialSearchType === MATERIAL_SEARCH_TYPES.so ? (
                                  <table
                                    className="table table-sm table-bordered mb-0"
                                    style={{ minWidth: '760px' }}
                                  >
                                    <thead>
                                      <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                        <th style={{ fontSize: '11px', width: '36px' }}>
                                          <input
                                            type="checkbox"
                                            checked={
                                              getSelectableMaterialSearchKeys().length > 0 &&
                                              getSelectableMaterialSearchKeys().every((key) =>
                                                selectedMaterialSearchKeys.has(key)
                                              )
                                            }
                                            onChange={toggleSelectAllMaterialSearchResults}
                                            title="Select all not yet added"
                                          />
                                        </th>
                                        <th style={{ fontSize: '11px' }}>
                                          {materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                            ? 'RFQ No'
                                            : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                              ? 'SQ No'
                                              : 'SO No'}
                                        </th>
                                        <th style={{ fontSize: '11px' }}>Summary</th>
                                        <th style={{ fontSize: '11px' }}>Status</th>
                                        <th style={{ fontSize: '11px' }}>Materials</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {materialSearchResults.map((item, idx) => {
                                        const key = getMaterialSearchKey(item, idx)
                                        const alreadyAdded = isMaterialSearchItemAdded(item)
                                        const isSelected = selectedMaterialSearchKeys.has(key)
                                        const materialCount = Array.isArray(item.materials)
                                          ? item.materials.length
                                          : 0
                                        const numberLabel =
                                          materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                            ? item.rfqNumber || item.rfqNo || '-'
                                            : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                              ? item.quoteNumber || '-'
                                              : item.cpoNumber || '-'
                                        const summaryLabel =
                                          materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                            ? item.rfqSummary || item.summary || '-'
                                            : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                              ? item.quoteSummary || item.summary || '-'
                                              : item.cpoSummary || item.summary || '-'
                                        const statusLabel =
                                          materialSearchType === MATERIAL_SEARCH_TYPES.rfq
                                            ? item.rfqStatus || item.status || '-'
                                            : materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                              ? item.quoteStatus || item.status || '-'
                                              : item.cpoStatus || item.status || '-'
                                        return (
                                          <tr
                                            key={key}
                                            style={{
                                              backgroundColor: alreadyAdded
                                                ? '#e8f5e9'
                                                : isSelected
                                                  ? '#e8f4f8'
                                                  : undefined
                                            }}
                                          >
                                            <td className="text-center">
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleMaterialSearchSelection(key)}
                                                disabled={alreadyAdded}
                                              />
                                            </td>
                                            <td style={{ fontSize: '12px' }}>
                                              {numberLabel}
                                              {alreadyAdded && (
                                                <span
                                                  className="badge badge-success ml-1"
                                                  style={{ fontSize: '10px' }}
                                                >
                                                  Added
                                                </span>
                                              )}
                                            </td>
                                            <td
                                              style={{
                                                fontSize: '12px',
                                                maxWidth: '220px',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word'
                                              }}
                                            >
                                              {summaryLabel}
                                            </td>
                                            <td style={{ fontSize: '12px' }}>{statusLabel}</td>
                                            <td style={{ fontSize: '12px' }}>{materialCount}</td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                ) : (
                                  <table
                                    className="table table-sm table-bordered mb-0"
                                    style={{ minWidth: '980px' }}
                                  >
                                    <thead>
                                      <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                        <th style={{ fontSize: '11px', width: '36px' }}>
                                          <input
                                            type="checkbox"
                                            checked={
                                              getSelectableMaterialSearchKeys().length > 0 &&
                                              getSelectableMaterialSearchKeys().every((key) =>
                                                selectedMaterialSearchKeys.has(key)
                                              )
                                            }
                                            onChange={toggleSelectAllMaterialSearchResults}
                                            title="Select all not yet added"
                                          />
                                        </th>
                                        <th style={{ fontSize: '11px' }}>Ref. Number</th>
                                        <th style={{ fontSize: '11px' }}>Material Code</th>
                                        <th style={{ fontSize: '11px' }}>Material Description</th>
                                        <th style={{ fontSize: '11px' }}>HSN</th>
                                        <th style={{ fontSize: '11px' }}>UOM</th>
                                        <th style={{ fontSize: '11px' }}>Quantity</th>
                                        <th style={{ fontSize: '11px', minWidth: '140px' }}>
                                          Quotation Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {materialSearchResults.map((item, idx) => {
                                        const key = getMaterialSearchKey(item, idx)
                                        const alreadyAdded = isMaterialSearchItemAdded(item)
                                        const isSelected = selectedMaterialSearchKeys.has(key)
                                        const quotationStatus = getProcurementQuotationStatus(item)
                                        return (
                                          <tr
                                            key={key}
                                            style={{
                                              backgroundColor: alreadyAdded
                                                ? '#e8f5e9'
                                                : isSelected
                                                  ? '#e8f4f8'
                                                  : undefined
                                            }}
                                          >
                                            <td className="text-center">
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleMaterialSearchSelection(key)}
                                                disabled={alreadyAdded}
                                                title={
                                                  alreadyAdded
                                                    ? 'Already added to materials'
                                                    : ''
                                                }
                                              />
                                            </td>
                                            <td style={{ fontSize: '12px' }}>
                                              {item.prNo || item.prNumber || '-'}
                                            </td>
                                            <td style={{ fontSize: '12px' }}>
                                              {item.materialCode || '-'}
                                            </td>
                                            <td
                                              style={{
                                                fontSize: '12px',
                                                maxWidth: '180px',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word'
                                              }}
                                            >
                                              {item.materialDescription || '-'}
                                            </td>
                                            <td style={{ fontSize: '12px' }}>
                                              {item.hsnCode || item.hsn || '-'}
                                            </td>
                                            <td style={{ fontSize: '12px' }}>{item.uom || '-'}</td>
                                            <td style={{ fontSize: '12px' }}>
                                              {item.quantity ?? '-'}
                                              {alreadyAdded && (
                                                <span
                                                  className="badge badge-success ml-1"
                                                  style={{ fontSize: '10px' }}
                                                >
                                                  Added
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ fontSize: '12px' }}>
                                              {quotationStatus || '-'}
                                            </td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                         <div style={{ overflowX: "auto" }}>
                              <table
                                className="table table-bordered table-hover mb-3"
                                style={{ minWidth: "1740px" }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor: "#003366",
                                      color: "#fff",
                                    }}
                                  >
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        width: "56px",
                                      }}
                                    >
                                      S.No
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "120px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Ref. Number
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "130px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Material Code
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "220px",
                                      }}
                                    >
                                      Material Description
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "110px",
                                      }}
                                    >
                                      HSN
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "100px",
                                      }}
                                    >
                                      UOM
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "90px",
                                      }}
                                    >
                                      Quantity
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "100px",
                                      }}
                                    >
                                      Basic Price
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "90px",
                                      }}
                                    >
                                      GST %
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "100px",
                                      }}
                                    >
                                      GST Value
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "100px",
                                      }}
                                    >
                                      Total Value
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "180px",
                                      }}
                                    >
                                      Technical Remarks
                                    </th>
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        width: "90px",
                                      }}
                                    >
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {materials.map((row, index) => {
                                    return (
                                      <tr
                                        key={index}
                                        onClick={() =>
                                          setActiveMaterialRowIndex(index)
                                        }
                                        style={{
                                          cursor: "pointer",
                                          backgroundColor:
                                            activeMaterialRowIndex === index
                                              ? "#e8f4f8"
                                              : undefined,
                                        }}
                                      >
                                        <td>{index + 1}</td>
                                        <td style={{ minWidth: "120px" }}>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.referenceNumber || ""}
                                            onChange={(e) =>
                                              updateMaterial(
                                                index,
                                                "referenceNumber",
                                                e.target.value,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder="Ref. No"
                                            style={{ minWidth: "110px" }}
                                          />
                                        </td>
                                        <td style={{ minWidth: "130px" }}>
                                          <div
                                            style={{
                                              position: "relative",
                                              width: "100%",
                                              minWidth: "120px",
                                            }}
                                          >
                                            <input
                                              ref={(el) =>
                                                (materialCodeInputRefs.current[
                                                  index
                                                ] = el)
                                              }
                                              type="text"
                                              className="form-control form-control-sm"
                                              placeholder={
                                                isMaterialCatalogLoading
                                                  ? "Loading materials..."
                                                  : "Search material..."
                                              }
                                              value={
                                                materialCodeSearchTerms[
                                                  index
                                                ] ?? row.materialCode
                                              }
                                              onChange={(e) =>
                                                handleMaterialCodeSearchChange(
                                                  index,
                                                  e.target.value,
                                                )
                                              }
                                              onKeyDown={(e) =>
                                                handleMaterialCodeKeyDown(
                                                  index,
                                                  e,
                                                )
                                              }
                                              onFocus={() =>
                                                openMaterialCodeDropdown(index)
                                              }
                                              onBlur={() =>
                                                setTimeout(
                                                  () =>
                                                    closeMaterialCodeDropdown(
                                                      index,
                                                    ),
                                                  200,
                                                )
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              autoComplete="off"
                                              disabled={
                                                isMaterialCatalogLoading
                                              }
                                              style={{
                                                width: "100%",
                                                paddingRight: isMaterialCatalogLoading ? "26px" : undefined,
}}
                                      
                                            />
                                            {isMaterialCatalogLoading && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  right: "8px",
                                                  top: "50%",
                                                  transform: "translateY(-50%)",
                                                  pointerEvents: "none",
                                                }}
                                              >
                                                <PulseLoader
                                                  color="#1199B2"
                                                  size={4}
                                                />
                                              </div>
                                            )}
                                         
                                          </div>
                                        </td>
                                       <td style={{ verticalAlign: "top", minWidth: "220px" }}>
                                         <textarea
                                          ref={(el) => (materialDescInputRefs.current[index] = el)}
                                          className="form-control form-control-sm"
                                          rows={2}
                                          placeholder={
                                          isMaterialCatalogLoading ? "Loading materials..." : "Search material description..."
                                           }
                                          value={row.materialDescription}
                                          onChange={(e) => handleMaterialDescSearchChange(index, e.target.value)}
                                          onKeyDown={(e) => handleMaterialDescKeyDown(index, e)}
                                          onFocus={() => openMaterialDescDropdown(index)}
                                          onBlur={() => setTimeout(() => closeMaterialDescDropdown(index), 200)}
                                          onClick={(e) => e.stopPropagation()}
                                          disabled={isMaterialCatalogLoading}
                                          style={{
                                          width: "100%",
                                          minWidth: "200px",
                                          resize: "vertical",
                                          whiteSpace: "pre-wrap",
                                           wordBreak: "break-word",
                                           }}
                                          />
                                      </td>
                                        <td style={{ minWidth: "110px" }}>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.hsn}
                                            onChange={(e) =>
                                              updateMaterial(
                                                index,
                                                "hsn",
                                                e.target.value,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ minWidth: "100px" }}
                                          />
                                        </td>
                                        <td style={{ minWidth: "100px" }}>
                                          <select
                                            className="form-control form-control-sm"
                                            value={row.uom || ''}
                                            onChange={(e) =>
                                              updateMaterial(
                                                index,
                                                'uom',
                                                e.target.value
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isLoading || isLoadingUnits}
                                            style={{ minWidth: '90px' }}
                                          >
                                            <option value="">
                                              {isLoadingUnits ? 'Loading...' : 'UOM'}
                                            </option>
                                            {row.uom &&
                                              !unitOptions.some(
                                                (opt) =>
                                                  opt.unit.toLowerCase() ===
                                                  String(row.uom).toLowerCase()
                                              ) && (
                                                <option value={row.uom}>
                                                  {row.uom}
                                                </option>
                                              )}
                                            {unitOptions.map((opt) => (
                                              <option
                                                key={opt.id || opt.unit}
                                                value={opt.unit}
                                              >
                                                {opt.unit}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.quantity}
                                            onChange={(e) =>
                                              handleQuantityChange(
                                                index,
                                                e.target.value,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                            value={row.basicPrice || ""}
                                            onChange={(e) =>
                                              handleMaterialDecimalChange(
                                                index,
                                                "basicPrice",
                                                e.target.value,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </td>
                                       <td>
                                         <select
                                           className="form-control form-control-sm"
                                           value={row.gst || DEFAULT_PR_GST_PERCENT}
                                           onChange={(e) =>
                                           handleGstChange(index, "gst", e.target.value) }>
                                           <option value="">Select GST</option>
                                           <option value="0">0%</option>
                                           <option value="5">5%</option>
                                           <option value="18">18%</option>
                                           <option value="28">28%</option>
                                          </select>
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.gstValue || ""}
                                            readOnly
                                            style={{
                                              backgroundColor: "#f8f9fa",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.totalValue || ""}
                                            readOnly
                                            style={{
                                              backgroundColor: "#f8f9fa",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </td>
                                        <td
                                          style={{
                                            verticalAlign: "top",
                                            minWidth: "180px",
                                          }}
                                        >
                                          <textarea
                                            className="form-control form-control-sm"
                                            rows={2}
                                            value={row.technicalRemarks || ""}
                                            onChange={(e) =>
                                              updateMaterial(
                                                index,
                                                "technicalRemarks",
                                                e.target.value,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder="Technical remarks"
                                            style={{
                                              resize: "vertical",
                                              minWidth: "160px",
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word",
                                            }}
                                          />
                                        </td>
                                        <td
                                          className="text-center"
                                          style={{ whiteSpace: "nowrap" }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div
                                            className="btn-group"
                                            role="group"
                                          >
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-success"
                                              onClick={() =>
                                                addMaterialRowAfter(index)
                                              }
                                              title="Add row"
                                              style={{ padding: "3px 8px" }}
                                            >
                                              <i
                                                className="fa-solid fa-plus"
                                                aria-hidden="true"
                                              />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-danger"
                                              onClick={() =>
                                                removeMaterialRow(index)
                                              }
                                              disabled={materials.length <= 1}
                                              title="Delete row"
                                              style={{ padding: "3px 8px" }}
                                            >
                                              <i
                                                className="fa-solid fa-trash-can"
                                                aria-hidden="true"
                                              />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {getFilledMaterials().length > 0 && (
                                    <tr style={{ backgroundColor: "#f4f6f9" }}>
                                      <td
                                        colSpan={7}
                                        className="text-right align-middle"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Gross Total
                                        {poDetails.poCurrency
                                          ? ` (${poDetails.poCurrency})`
                                          : ""}
                                      </td>
                                      <td
                                        className="align-middle"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatMaterialAmount(
                                          materialGrossTotals.grossTotalBasicPrice,
                                        )}
                                      </td>
                                      <td />
                                      <td
                                        className="align-middle"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatMaterialAmount(
                                          materialGrossTotals.grossGstValue,
                                        )}
                                      </td>
                                      <td
                                        className="align-middle"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatMaterialAmount(
                                          materialGrossTotals.grossTotalValue,
                                        )}
                                      </td>
                                      <td colSpan={2} />
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            {materials.map((row, index) => {
                              if (!showMaterialCodeDropdown[index]) return null;
                              const pos = materialCodeDropdownPos[index];
                              if (!pos) return null;

                              const filtered =
                                getFilteredMaterialCatalog(index);

                              return createPortal(
                                <div
                                  key={`material-code-dd-${index}`}
                                  style={{
                                    position: "fixed",
                                    top: pos.top,
                                    left: pos.left,
                                    width: pos.width,
                                    maxHeight: "240px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 20000,
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                  }}
                                >
                                  {filtered.length > 0
                                    ? filtered.map((material, mi) => (
                                        <div
                                          key={material._id}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleMaterialCodeSelect(
                                              index,
                                              material,
                                            );
                                          }}
                                          onMouseEnter={() =>
                                            setSelectedMaterialCodeIndex(
                                              (prev) => ({
                                                ...prev,
                                                [index]: mi,
                                              }),
                                            )
                                          }
                                          style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #f0f0f0",
                                            backgroundColor:
                                              mi ===
                                              (selectedMaterialCodeIndex[
                                                index
                                              ] ?? -1)
                                                ? "#e9ecef"
                                                : "#fff",
                                          }}
                                        >
                                          <strong style={{ fontSize: "13px" }}>
                                            {material.materialCode}
                                          </strong>
                                        </div>
                                      ))
                                    : materialCodeSearchTerms[index] && (
                                        <div
                                          style={{
                                            padding: "8px 12px",
                                            color: "#6c757d",
                                            fontSize: "13px",
                                          }}
                                        >
                                          No materials found
                                        </div>
                                      )}
                                </div>,
                                document.body,
                              );
                            })}

                           {materials.map((row, index) => {
                              if (!showMaterialDescDropdown[index]) return null;
                              const pos = materialDescDropdownPos[index];
                              if (!pos) return null;

                              const filtered =
                                getFilteredMaterialDescCatalog(index);

                              return createPortal(
                                <div
                                  key={`material-desc-dd-${index}`}
                                  style={{
                                    position: "fixed",
                                    top: pos.top,
                                    left: pos.left,
                                    width: pos.width,
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 20000,
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                  }}
                                >
                                  {filtered.length > 0 ? (
                                    filtered.map((material, mi) => (
                                      <div
                                        key={material._id}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleMaterialDescSelect(
                                            index,
                                            material,
                                          );
                                        }}
                                        onMouseEnter={() =>
                                          setSelectedMaterialDescIndex(
                                            (prev) => ({
                                              ...prev,
                                              [index]: mi,
                                            }),
                                          )
                                        }
                                        style={{
                                          padding: "8px 12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                          backgroundColor:
                                            mi ===
                                            (selectedMaterialDescIndex[index] ??
                                              -1)
                                              ? "#e9ecef"
                                              : "#fff",
                                        }}
                                      >
                                        <strong style={{ fontSize: "13px" }}>
                                          {material.productName}
                                        </strong>
                                      </div>
                                    ))
                                  ) : (
                                    <div
                                      style={{
                                        padding: "8px 12px",
                                        color: "#6c757d",
                                        fontSize: "13px",
                                      }}
                                    >
                                      No materials found
                                    </div>
                                  )}
                                </div>,
                                document.body,
                              );
                            })}
                      </div>
                    )}

                    {activeTab === 'attachments' && (
                      <div>
                       

                        <input
                          ref={attachmentInputRef}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handleAttachmentFileSelect}
                        />
                        <button
                          type="button"
                          className="btn btn-blue mb-3"
                          onClick={() => attachmentInputRef.current?.click()}
                          disabled={
                            poOwnerAttachments.length + pendingAttachmentFiles.length >= MAX_PO_OWNER_ATTACHMENTS
                          }
                        >
                          <i className="fa-solid fa-paperclip mr-1" /> Choose Files
                        </button>

                        {poOwnerAttachments.map((att) => (
                          <div key={att.s3Key} className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ border: '1px solid #dee2e6', borderRadius: 6 }}>
                            <span>{att.originalFileName || att.s3Key}</span>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadPoOwnerAttachment(att.s3Key)}>
                              Download
                            </button>
                          </div>
                        ))}

                        {pendingAttachmentFiles.map((file, idx) => (
                          <div key={`${file.name}-${idx}`} className="d-flex justify-content-between align-items-center p-2 mb-2" style={{ border: '1px dashed #cbd5e1', borderRadius: 6 }}>
                            <span>{file.name} ({formatFileSize(file.size)})</span>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePendingAttachment(idx)}>
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeTab === 'supplierMail' && (
                      <div>
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Supplier Name</label>
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search supplier..."
                                  value={supplierSearchTerm}
                                  onChange={handleSupplierSearchChange}
                                  onKeyDown={handleSupplierKeyDown}
                                  onFocus={() => setShowSupplierDropdown(true)}
                                  onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                                  disabled={isSuppliersLoading}
                                />
                                {showSupplierDropdown && filteredSuppliers.length > 0 && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: 0,
                                      right: 0,
                                      maxHeight: 200,
                                      overflowY: 'auto',
                                      backgroundColor: '#fff',
                                      border: '1px solid #ced4da',
                                      borderRadius: 4,
                                      zIndex: 1000,
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                  >
                                    {filteredSuppliers.map((supplier, index) => (
                                      <div
                                        key={supplier._id}
                                        onMouseDown={(e) => {
                                          e.preventDefault()
                                          handleSupplierSelect(supplier)
                                        }}
                                        style={{
                                          padding: '8px 12px',
                                          cursor: 'pointer',
                                          backgroundColor: index === selectedSupplierIndex ? '#e9ecef' : '#fff'
                                        }}
                                      >
                                        {supplier.clientname}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {supplierId && (
                                <small className="form-text text-muted">
                                  Selected: {supplierName}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
<div className="form-group">
  <label>Supplier Mail ID</label>
  <div style={{ position: 'relative' }} ref={supplierEmailPanelRef}>
    <input
      type="text"
      className="form-control"
      placeholder="Select supplier email(s)"
      value={supplierMailId}
      onChange={(e) => setSupplierMailId(e.target.value)}
      onFocus={() => {
        if (supplierContacts.length > 0) {
          setShowSupplierEmailDropdown(true)
        }
      }}
      readOnly={supplierContacts.length > 0}
    />
    {showSupplierEmailDropdown && supplierContacts.length > 0 && (
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: 240,
          overflowY: 'auto',
          backgroundColor: '#fff',
          border: '1px solid #ced4da',
          borderRadius: 4,
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        {supplierContacts.map((contact, index) => (
          <div
            key={`${contact.email}-${index}`}
            className="d-flex align-items-center justify-content-between"
            role="button"
            tabIndex={0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => toggleSupplierContactEmail(contact.email)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleSupplierContactEmail(contact.email)
              }
            }}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: index === supplierContacts.length - 1 ? 'none' : '1px solid #f0f0f0'
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>
                {contact.contactPerson || contact.email}
              </div>
              <div style={{ fontSize: '12px', color: '#495057' }}>{contact.email}</div>
            </div>
            <input
              type="checkbox"
              checked={selectedSupplierEmails.has(contact.email)}
              readOnly
              tabIndex={-1}
              style={{ cursor: 'pointer', pointerEvents: 'none' }}
            />
          </div>
        ))}
      </div>
    )}
  </div>
</div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label>Approver</label>
                              <select
                                className="form-control"
                                value={selectedApproverId}
                                onChange={(e) => setSelectedApproverId(e.target.value)}
                                disabled={isPoUsersLoading || isLoading}
                              >
                                <option value="">
                                  {isPoUsersLoading
                                    ? 'Loading users...'
                                    : approverUsers.length === 0
                                      ? 'No approvers available'
                                      : 'Select approver'}
                                </option>
                                {approverUsers.map((user) => (
                                  <option key={user._id} value={user._id}>
                                    {user.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'milestone' && (
                      <div>
                        {milestones.length === 0 ? (
                          <div className="text-center py-4 mb-3" style={{ border: '1px dashed #dee2e6', borderRadius: 4 }}>
                            <p className="text-muted mb-2">No milestones added.</p>
                            <button type="button" className="btn btn-sm btn-blue" onClick={() => setMilestones([emptyMilestone()])}>
                              <i className="fa-solid fa-plus mr-1" /> Add Milestone
                            </button>
                          </div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table
                              className="table table-bordered table-hover mb-0"
                              style={{ tableLayout: 'fixed', width: '100%', minWidth: '980px' }}
                            >
                              <colgroup>
                                <col style={{ width: '48px' }} />
                                <col style={{ width: '250px' }} />
                                <col style={{ width: '250px' }} />
                                <col style={{ width: '200px' }} />
                                <col />
                                <col style={{ width: '72px' }} />
                              </colgroup>
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>Milestone Activity</th>
                                  <th style={{ fontSize: '12px' }}>Action to be Taken</th>
                                  <th style={{ fontSize: '12px' }}>Responsibility</th>
                                  <th style={{ fontSize: '12px' }}>Attachments</th>
                                  <th style={{ fontSize: '12px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {milestones.map((row, index) => (
                                  <tr key={index}>
                                    <td style={{ verticalAlign: 'top' }}>{index + 1}</td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.milestoneActivity}
                                        onChange={(e) =>
                                          updateMilestone(index, 'milestoneActivity', e.target.value)
                                        }
                                      />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.actionToBeTaken}
                                        onChange={(e) =>
                                          updateMilestone(index, 'actionToBeTaken', e.target.value)
                                        }
                                      />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <select
                                        className="form-control form-control-sm"
                                        value={row.responsibilityUserId}
                                        onChange={(e) =>
                                          updateMilestone(index, 'responsibilityUserId', e.target.value)
                                        }
                                        disabled={isPoUsersLoading}
                                      >
                                        <option value="">
                                          {isPoUsersLoading
                                            ? 'Loading...'
                                            : poUsers.length === 0
                                              ? 'No users available'
                                              : 'Select user'}
                                        </option>
                                        {poUsers.map((user) => (
                                          <option key={user._id} value={user._id}>
                                            {user.name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                      <div>
                                        <div
                                          className="d-flex align-items-center flex-wrap mb-1"
                                          style={{ gap: '6px' }}
                                        >
                                          <input
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            ref={(el) => {
                                              milestoneAttachmentInputRefs.current[index] = el
                                            }}
                                            onChange={(e) =>
                                              handleMilestoneAttachmentFileSelect(index, e)
                                            }
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-blue"
                                            onClick={() =>
                                              milestoneAttachmentInputRefs.current[index]?.click()
                                            }
                                            disabled={
                                              isUploadingMilestoneAttachments ||
                                              getMilestoneAttachmentCount(row) >=
                                                MAX_PO_MILESTONE_ATTACHMENTS
                                            }
                                            style={{ whiteSpace: 'nowrap' }}
                                          >
                                            <i className="fa-solid fa-paperclip mr-1" />
                                            Choose
                                          </button>
                                          <small className="text-muted">
                                            {getMilestoneAttachmentCount(row)}/
                                            {MAX_PO_MILESTONE_ATTACHMENTS}
                                          </small>
                                        </div>

                                        {(row.attachments || []).map((att, attIndex) => (
                                          <div
                                            key={`${att.s3Key}-${attIndex}`}
                                            className="d-flex align-items-center justify-content-between py-1"
                                            style={{
                                              borderBottom: '1px solid #eef1f4',
                                              gap: '6px'
                                            }}
                                          >
                                            <span
                                              className="text-truncate"
                                              style={{ fontSize: '11px', maxWidth: '140px' }}
                                              title={att.originalFileName || att.s3Key}
                                            >
                                              {att.originalFileName || att.s3Key}
                                            </span>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                              <button
                                                type="button"
                                                className="btn btn-xs btn-outline-primary mr-1"
                                                onClick={() => downloadMilestoneAttachment(att.s3Key)}
                                                style={{ padding: '1px 6px', fontSize: '10px' }}
                                              >
                                                <i className="fa-solid fa-download" />
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-xs btn-outline-danger"
                                                onClick={() =>
                                                  removeMilestoneAttachment(index, attIndex)
                                                }
                                                style={{ padding: '1px 6px', fontSize: '10px' }}
                                              >
                                                <i className="fa-solid fa-trash-can" />
                                              </button>
                                            </div>
                                          </div>
                                        ))}

                                        {(row.pendingFiles || []).map((file, fileIndex) => (
                                          <div
                                            key={`${file.name}-${fileIndex}`}
                                            className="d-flex align-items-center justify-content-between py-1"
                                            style={{
                                              borderBottom: '1px dashed #dbe3ea',
                                              gap: '6px'
                                            }}
                                          >
                                            <span
                                              className="text-truncate"
                                              style={{ fontSize: '11px', maxWidth: '140px' }}
                                              title={file.name}
                                            >
                                              {file.name}
                                            </span>
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-outline-danger"
                                              onClick={() =>
                                                removeMilestonePendingFile(index, fileIndex)
                                              }
                                              style={{ padding: '1px 6px', fontSize: '10px' }}
                                            >
                                              <i className="fa-solid fa-trash-can" />
                                            </button>
                                          </div>
                                        ))}

                                        {getMilestoneAttachmentCount(row) === 0 && (
                                          <small className="text-muted" style={{ fontSize: '11px' }}>
                                            No files
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => addMilestoneRowAfter(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-plus" aria-hidden="true" />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => removeMilestoneRow(index)}
                                          style={{ padding: '3px 8px' }}
                                        >
                                          <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'preview' && canAccessPreview && (
                      <div>
                        <div className="row mb-3">
                          <div className="col-md-3"><strong>PO Number:</strong> {poDetails.poNumber}</div>
                          <div className="col-md-3"><strong>PO Date:</strong> {formatDateForApi(poDetails.poDate)}</div>
                          <div className="col-md-3"><strong>Delivery Date:</strong> {formatDateForApi(poDetails.poDeliveryDate)}</div>
                          <div className="col-md-3"><strong>Currency:</strong> {poDetails.poCurrency}</div>
                        </div>
                        <div className="mb-3"><strong>Summary:</strong> {poDetails.poSummary || '-'}</div>
                        <div className="row mb-3">
                          <div className="col-md-4">
                            <strong>Supplier Name:</strong> {supplierName || '-'}
                          </div>
                          <div className="col-md-4">
                            <strong>Supplier Mail ID:</strong> {supplierMailId || '-'}
                          </div>
                          <div className="col-md-4">
                            <strong>Approver:</strong>{' '}
                            {approverUsers.find((user) => user._id === selectedApproverId)?.name || '-'}
                          </div>
                        </div>

                        <div className="mb-2 mt-3">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-list-check mr-2" />
                            Terms
                          </h5>
                        </div>
                        {terms.filter((t) => String(t.term || '').trim()).length === 0 ? (
                          <div className="text-muted mb-3">No terms</div>
                        ) : (
                          <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm mb-0">
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                                  <th style={{ fontSize: '12px', minWidth: '160px' }}>Term</th>
                                  <th style={{ fontSize: '12px', minWidth: '200px' }}>Conditions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {terms
                                  .filter((t) => String(t.term || '').trim())
                                  .map((term, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {term.term || '-'}
                                      </td>
                                      <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {term.response || '-'}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="mb-2 mt-3">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-boxes-stacked mr-2" />
                            Materials
                          </h5>
                        </div>
                        {getFilledMaterials().length === 0 ? (
                          <div className="text-muted mb-3">No materials</div>
                        ) : (
                          <div className="table-responsive mb-4">
                            <table
                              className="table table-bordered table-sm mb-0"
                              style={{ minWidth: '1200px' }}
                            >
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>Material Code</th>
                                  <th style={{ fontSize: '12px' }}>Material Description</th>
                                  <th style={{ fontSize: '12px' }}>Ref. Number</th>
                                  <th style={{ fontSize: '12px' }}>HSN</th>
                                  <th style={{ fontSize: '12px' }}>UOM</th>
                                  <th style={{ fontSize: '12px' }}>Quantity</th>
                                  <th style={{ fontSize: '12px' }}>Basic Price</th>
                                  <th style={{ fontSize: '12px' }}>GST %</th>
                                  <th style={{ fontSize: '12px' }}>GST Value</th>
                                  <th style={{ fontSize: '12px' }}>Total Value</th>
                                  <th style={{ fontSize: '12px' }}>Technical Remarks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getFilledMaterials().map((row, index) => {
                                  const pricing = calcMaterialPricing(
                                    row.basicPrice,
                                    row.quantity,
                                    row.gst
                                  )
                                  return (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>{row.materialCode || '-'}</td>
                                      <td
                                        style={{
                                          maxWidth: '240px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {row.materialDescription || '-'}
                                      </td>
                                      <td>{row.referenceNumber || '-'}</td>
                                      <td>{row.hsn || '-'}</td>
                                      <td>{row.uom || '-'}</td>
                                      <td>{row.quantity || '-'}</td>
                                      <td>{formatMaterialAmount(row.basicPrice)}</td>
                                      <td>
                                        {row.gst !== '' && row.gst != null ? `${row.gst}%` : '-'}
                                      </td>
                                      <td>
                                        {row.gstValue !== '' && row.gstValue != null
                                          ? formatMaterialAmount(row.gstValue)
                                          : formatMaterialAmount(pricing.gstValue)}
                                      </td>
                                      <td>
                                        {row.totalValue !== '' && row.totalValue != null
                                          ? formatMaterialAmount(row.totalValue)
                                          : formatMaterialAmount(pricing.totalValue)}
                                      </td>
                                      <td
                                        style={{
                                          maxWidth: '200px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {row.technicalRemarks || '-'}
                                      </td>
                                    </tr>
                                  )
                                })}
                                <tr style={{ backgroundColor: '#f4f6f9' }}>
                                  <td
                                    colSpan={7}
                                    className="text-right align-middle"
                                    style={{ fontSize: '13px', fontWeight: 600 }}
                                  >
                                    Gross Total
                                    {poDetails.poCurrency ? ` (${poDetails.poCurrency})` : ''}
                                  </td>
                                  <td
                                    className="align-middle"
                                    style={{ fontSize: '13px', fontWeight: 600 }}
                                  >
                                    {formatMaterialAmount(materialGrossTotals.grossTotalBasicPrice)}
                                  </td>
                                  <td />
                                  <td
                                    className="align-middle"
                                    style={{ fontSize: '13px', fontWeight: 600 }}
                                  >
                                    {formatMaterialAmount(materialGrossTotals.grossGstValue)}
                                  </td>
                                  <td
                                    className="align-middle"
                                    style={{ fontSize: '13px', fontWeight: 600 }}
                                  >
                                    {formatMaterialAmount(materialGrossTotals.grossTotalValue)}
                                  </td>
                                  <td />
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="mb-2 mt-3">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-flag-checkered mr-2" />
                            Milestone
                          </h5>
                        </div>
                        {milestones.filter(
                          (m) =>
                            String(m.milestoneActivity || '').trim() ||
                            String(m.actionToBeTaken || '').trim() ||
                            String(m.responsibilityUser || '').trim()
                        ).length === 0 ? (
                          <div className="text-muted mb-3">No milestones</div>
                        ) : (
                          <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm mb-0">
                              <thead>
                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                  <th style={{ fontSize: '12px' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>Milestone Activity</th>
                                  <th style={{ fontSize: '12px' }}>Action to be Taken</th>
                                  <th style={{ fontSize: '12px' }}>Responsibility</th>
                                  <th style={{ fontSize: '12px' }}>Attachments</th>
                                </tr>
                              </thead>
                              <tbody>
                                {milestones.map((row, index) => {
                                  const milestoneAttachments = Array.isArray(row.attachments)
                                    ? row.attachments
                                    : []
                                  const pendingFiles = Array.isArray(row.pendingFiles)
                                    ? row.pendingFiles
                                    : []
                                  return (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {row.milestoneActivity || '-'}
                                      </td>
                                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {row.actionToBeTaken || '-'}
                                      </td>
                                      <td style={{ whiteSpace: 'nowrap' }}>
                                        {row.responsibilityUser || '-'}
                                      </td>
                                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {milestoneAttachments.length + pendingFiles.length === 0 ? (
                                          '-'
                                        ) : (
                                          <ul className="mb-0 pl-3">
                                            {milestoneAttachments.map((att, attIndex) => (
                                              <li key={`${att.s3Key || 'att'}-${attIndex}`}>
                                                {att.originalFileName || att.s3Key || 'Attachment'}
                                              </li>
                                            ))}
                                            {pendingFiles.map((file, fileIndex) => (
                                              <li key={`${file.name || 'file'}-${fileIndex}`}>
                                                {file.name || 'Pending file'}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-file-pdf mr-2 text-primary" />
                            Preview PDF
                          </div>
                          <div className="p-3">
                            {hasPoPdf ? (
                              <>
                                <div className="mb-3">
                                  <a
                                    href={pdfPreviewUrl || poDetails.pdfDownloadUrl || poDetails.pdfLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <i className="fa-solid fa-up-right-from-square mr-1" />
                                    Open PDF in new tab
                                  </a>
                                </div>
                                <div
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    minHeight: '420px'
                                  }}
                                >
                                  {pdfPreviewUrl || poDetails.pdfDownloadUrl || poDetails.pdfLink ? (
                                    <iframe
                                      title="PO PDF Preview"
                                      src={pdfPreviewUrl || poDetails.pdfDownloadUrl || poDetails.pdfLink}
                                      style={{ width: '100%', height: '420px', border: 'none' }}
                                    />
                                  ) : (
                                    <p className="text-muted mb-0 p-3">
                                      Could not load PDF preview. Try again shortly.
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-muted mb-0">
                                PDF is being generated. It will appear here once available.
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          className="mt-3"
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              padding: '10px 14px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            <i className="fa-solid fa-user-check mr-2 text-primary" />
                            Send For Approval
                          </div>
                          <div className="p-3">
                            <div className="form-group mb-2">
                              <label>Approver</label>
                              <div className="txtdsply" style={{ fontSize: '13px' }}>
                                {approverUsers.find((user) => user._id === selectedApproverId)?.name || '-'}
                              </div>
                            </div>
                            <div className="form-group mb-2">
                              <label>To Email</label>
                              <div className="txtdsply" style={{ fontSize: '13px' }}>
                                {approverUsers.find((user) => user._id === selectedApproverId)?.email || '-'}
                              </div>
                            </div>
                            {userEmail ? (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply" style={{ fontSize: '13px' }}>
                                  {userEmail}
                                </div>
                              </div>
                            ) : (
                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div className="txtdsply text-muted" style={{ fontSize: '13px' }}>
                                  Profile email not available — add CC addresses below
                                </div>
                              </div>
                            )}
                            <div className="form-group mb-3">
                              <label>Additional CC Emails</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. manager@example.com, cc@company.com"
                                value={additionalCcEmails}
                                onChange={(e) => setAdditionalCcEmails(e.target.value)}
                                disabled={isSendingForApproval}
                              />
                              <small className="form-text text-muted">
                                Comma-separated. Sent as CC along with your email.
                              </small>
                              {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                                <small className="form-text text-muted d-block">
                                  Will CC: {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                                </small>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={sendPoForApproval}
                              disabled={
                                isSendingForApproval ||
                                isPoUsersLoading ||
                                !selectedApproverId ||
                                !approverUsers.find((user) => user._id === selectedApproverId)?.email
                              }
                            >
                              {isSendingForApproval ? 'Sending...' : 'Send Approval'}
                            </button>
                          </div>
                        </div>

                        <div className="text-center mt-4">
                          <Link to="/po" className="btn btn-blue">Back to PO List</Link>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="card-footer d-flex justify-content-center align-items-center">
                    {activeTab === 'details' && (
                      <button type="button" className="btn btn-blue" onClick={() => goToTab('materials')} disabled={isLoading}>
                        Next
                      </button>
                    )}
                    {activeTab === 'materials' && (
                      <>
                        <button type="button" className="btn btn-blue mr-2" onClick={() => goToTab('details')} disabled={isLoading}>Previous</button>
                        <button type="button" className="btn btn-blue" onClick={() => goToTab('attachments')} disabled={isLoading}>Next</button>
                      </>
                    )}
                    {activeTab === 'attachments' && (
                      <>
                        <button type="button" className="btn btn-blue mr-2" onClick={() => goToTab('materials')} disabled={isLoading || isUploadingAttachments}>Previous</button>
                        <button type="button" className="btn btn-blue" onClick={() => goToTab('milestone')} disabled={isLoading || isUploadingAttachments}>
                          {isUploadingAttachments ? 'Uploading...' : 'Next'}
                        </button>
                      </>
                    )}
                    {activeTab === 'milestone' && (
                      <>
                        <button type="button" className="btn btn-blue mr-2" onClick={() => goToTab('attachments')} disabled={isLoading}>Previous</button>
                        <button type="button" className="btn btn-blue" onClick={() => goToTab('supplierMail')} disabled={isLoading}>
                          Next
                        </button>
                      </>
                    )}
                    {activeTab === 'supplierMail' && (
                      <>
                        <button type="button" className="btn btn-blue mr-2" onClick={() => goToTab('milestone')} disabled={isLoading}>Previous</button>
                        <button
                          type="submit"
                          className="btn btn-blue"
                          disabled={
                            isLoading ||
                            isLoadingPo ||
                            isFetchingPoNumber ||
                            isUploadingMilestoneAttachments ||
                            !poDetails.poNumber
                          }
                        >
                          {isLoading ||isLoadingSupplierMailId
                            ? 'Submitting...'
                            : isPoActive
                              ? 'Update PO'
                              : 'Activate PO'}
                        </button>
                      </>
                    )}
                    {activeTab === 'preview' && (
                      <button type="button" className="btn btn-blue" onClick={() => goToTab('supplierMail')}>Previous</button>
                    )}
                  </div>
                  </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Pocreate